import type { ChartCapabilityToolDef } from "./chartCapabilityTools";
import { mergeCitationNoteRecord, visibleCitationText } from "./citationNotes";
import { diag } from "./aiDiag";
import {
  listWorksheetsMetadata,
  readRangeContent,
  type ReadRangeContentResult,
  type WorksheetMetadata,
} from "./excelService";
import {
  createLinkedAbortController,
  mapWithConcurrencyLimit,
  type ExecutorWorkloadParticipant,
} from "./executorWorkload";
import {
  MAX_EXECUTOR_ITERATIONS,
  TABULAR_CELL_MAX_EXECUTABLE_ROWS,
  TABULAR_CELL_TRANSPORT_BATCH_SIZE,
  TABULAR_COLUMN_BATCH_SIZE,
  TABULAR_IDENTITY_LOOKUP_CONCURRENCY,
  TABULAR_MAX_USER_FILE_IDS_PER_TARGET,
  TABULAR_METADATA_MAX_TARGETS,
  clampTabularConcurrency,
  requiredExecutorIterationsForTabular,
  tabularRowsPerWave,
} from "./executorLimits";
import {
  TabularIdentityAuthError,
  resolveFactSetTabularIdentity,
  type FactSetTabularIdentityResolution,
} from "./idLookupWorkload";
import { executeModifyWorkbookTool } from "./executorWriteTools";
import { buildExcelLangfuseMetadata } from "./langfuseTrace";
import type {
  ExecutorToolRuntimeContext,
  RuntimeToolCallContext,
} from "./executorToolRuntime";
import { EXCEL_MAX_COLUMN, EXCEL_MAX_ROW } from "./excelCellAddress";
import {
  excelSerialFromIso,
  parseStrictIsoDateTime,
  probeExcelDateSystem,
  type ExcelDateSystem,
  type ExcelDateSystemProbeOptions,
} from "./excelDateSystem";
import type { ToolCallDetailedResult, ToolCallContext } from "./toolRegistry";
import {
  TabularQueryExecutionError,
  executeBatchQuery,
  executeCellBatchQuery,
  executeSingleQuery,
  getFunctionCallMetadata,
  resolveDataFormat,
  type ExecuteBatchQueryRequest,
  type ExecuteBatchQueryResponse,
  type ExecuteCellBatchQueryResponse,
  type ExecuteQueryResult,
  type ExecuteSingleQueryRequest,
  type GetFunctionCallMetadataRequest,
  type GetFunctionCallMetadataResponse,
  type ResolveDataFormatRequest,
  type ResolveDataFormatResponse,
  type TabularCompanyTarget,
  type TabularDataFormat,
  type TabularQueryOptions,
  type TabularResponseType,
} from "../shared/api/finster/tabularQueryExecution";

export const START_TABULAR_QUERY_WORKLOAD_TOOL_NAME =
  "start_tabular_query_workload";
export const NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME =
  "execute_next_tabular_query_wave";
export const COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME =
  "commit_tabular_query_wave";

export const TABULAR_QUERY_FAILURE_SENTINEL =
  "Something went wrong with the generation of this answer. Please try again or contact Support.";

const START_INPUT_KEYS = new Set([
  "stepId",
  "sheetName",
  "companyAddress",
  "companyIdAddress",
  "outputAddress",
  "prompt",
  "responseType",
  "tagOptions",
  "noMatchValue",
  "dataFormat",
]);
const DATA_FORMAT_KEYS = new Set([
  "formatType",
  "unit",
  "currency",
  "decimalPlaces",
  "dateFormat",
  "template",
]);
const RESPONSE_TYPES = new Set<TabularResponseType>([
  "TEXT",
  "NUMBER",
  "DATE",
  "BOOLEAN",
  "SINGLE_SELECT",
  "MULTI_SELECT",
]);
const FACTSET_PEOPLE_DATA_SOURCE = "FACTSET_PEOPLE";
const WEB_SEARCH_DATA_SOURCE = "WEB_SEARCH";
const USER_FILES_DATA_SOURCE = "USER_FILES";
const CROSS_COMPANY_SEARCH_FUNCTION = "all_company_search";
const FACTSET_STRUCTURED_DATA_FUNCTION = "get_factset_data";
/**
 * Sources whose figures the structured FactSet client answers in one batched
 * request per column batch, rather than one document search per row.
 */
const FACTSET_STRUCTURED_DATA_SOURCES = new Set([
  "FACTSET_FUNDAMENTALS",
  "MARKET_DATA",
  "ESTIMATES",
]);
const CURRENT_CEO_PROMPT_PATTERN =
  /\b(?:(?:current|latest|present)\s+(?:chief\s+executive\s+officer|ceo)|currently\s+(?:(?:serves?|serving)\s+as\s+|is\s+)?(?:the\s+)?(?:chief\s+executive\s+officer|ceo))\b/i;
const TABULAR_SCOPE_SCAN_PAGE_ROWS = 20_000;
const TABULAR_HOST_READ_RETRY_DELAYS_MS = [0, 75, 225] as const;
const TABULAR_WRITE_VERIFY_DELAYS_MS = [0, 75, 225] as const;
/**
 * Once this share of a wave comes back as service execution failures the query
 * service is down rather than short of evidence for these companies. Executing
 * the remaining waves would close every row against the outage in seconds, and
 * a closed row is never retried, so the workload stops and keeps them pending.
 */
const TABULAR_WAVE_SERVICE_FAILURE_STOP_RATIO = 0.5;
const TABULAR_SERVICE_UNAVAILABLE_REASON = "QUERY_SERVICE_UNAVAILABLE";
/**
 * Pauses before a wave that came back as service failures is executed again.
 * A restart or failover upstream outlives the per-request retries but not this
 * schedule, so it costs the run a pause instead of the remaining rows. Once the
 * schedule is spent the outage is long enough to hand back to the user.
 */
export const TABULAR_SERVICE_OUTAGE_RETRY_DELAYS_MS = [15_000, 45_000] as const;

/**
 * Column batch sizes that successfully completed after an oversized wave was
 * narrowed, keyed by the functions the query resolved to. A total outage may
 * also trigger smaller probes, but those unproven sizes must not penalize later
 * workloads after the service recovers.
 */
const learnedColumnBatchSizes = new Map<string, number>();

function columnBatchSizeKey(
  precomputedFunctionCalls: readonly { functionName: string }[],
): string {
  return precomputedFunctionCalls
    .map((call) => call.functionName)
    .sort()
    .join(",");
}

const nullableStringSchema = {
  type: ["string", "null"],
} as const;

const TABULAR_DATA_FORMAT_SCHEMA = {
  type: "object",
  properties: {
    formatType: {
      type: "string",
      enum: ["auto", "number", "currency", "percentage", "date", "template"],
    },
    unit: nullableStringSchema,
    currency: nullableStringSchema,
    decimalPlaces: {
      type: ["integer", "null"],
      minimum: 0,
      maximum: 6,
    },
    dateFormat: nullableStringSchema,
    template: nullableStringSchema,
  },
  required: [
    "formatType",
    "unit",
    "currency",
    "decimalPlaces",
    "dateFormat",
    "template",
  ],
  additionalProperties: false,
} as const;

export const START_TABULAR_QUERY_WORKLOAD_TOOL: ChartCapabilityToolDef = {
  name: START_TABULAR_QUERY_WORKLOAD_TOOL_NAME,
  description:
    "Register one aligned worksheet-column research workload. The runtime reads and " +
    "protects the company/output ranges, resolves exhaustive user requests against the " +
    "live populated company column, preflights one shared query, and keeps " +
    "all row values and results out of model context. Use this instead of finster_query " +
    "when one prompt must populate one output cell for every company row.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      stepId: {
        type: "string",
        description: "Existing run step that owns every workload write.",
      },
      sheetName: {
        type: "string",
        description: "Worksheet containing all aligned ranges.",
      },
      companyAddress: {
        type: "string",
        description:
          "Finite one-column company-label range without a header. For an all/every-row " +
          "user request, the runtime resolves its end against the live populated column.",
      },
      companyIdAddress: {
        ...nullableStringSchema,
        description:
          "Optional aligned one-column FactSet identifier range, or null.",
      },
      outputAddress: {
        type: "string",
        description:
          "Finite aligned one-column destination range; exhaustive requests are " +
          "expanded to the same runtime-resolved end row. Rows whose output cell " +
          "already holds a value or formula are skipped, so a range that is only " +
          "partly answered can be registered again.",
      },
      prompt: {
        type: "string",
        description: "One research question to apply to every company row.",
      },
      responseType: {
        type: "string",
        enum: Array.from(RESPONSE_TYPES),
      },
      tagOptions: {
        type: "array",
        maxItems: 100,
        items: { type: "string" },
        description:
          "Allowed values for select responses; otherwise an empty array.",
      },
      noMatchValue: {
        ...nullableStringSchema,
        description: "Optional service no-match value, or null.",
      },
      dataFormat: TABULAR_DATA_FORMAT_SCHEMA,
    },
    required: [
      "stepId",
      "sheetName",
      "companyAddress",
      "companyIdAddress",
      "outputAddress",
      "prompt",
      "responseType",
      "tagOptions",
      "noMatchValue",
      "dataFormat",
    ],
    additionalProperties: false,
  },
};

export const NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL: ChartCapabilityToolDef = {
  name: NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
  description:
    "Execute the next bounded active wave of the registered tabular workload. " +
    "The runtime resolves missing identities, executes batch or single requests, " +
    "and stores results in run state. Commit and close this wave before continuing.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      workloadId: {
        type: "string",
        description:
          "Workload ID returned by start_tabular_query_workload.",
      },
    },
    required: ["workloadId"],
    additionalProperties: false,
  },
};

export const COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL: ChartCapabilityToolDef = {
  name: COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
  description:
    "Guard, write, and exactly read back the active tabular wave. The runtime " +
    "verifies usable writes, leaves failed rows blank, and then closes the wave.",
  strict: true,
  inputSchema: {
    type: "object",
    properties: {
      workloadId: {
        type: "string",
        description:
          "Workload ID returned by start_tabular_query_workload.",
      },
    },
    required: ["workloadId"],
    additionalProperties: false,
  },
};

export const TABULAR_QUERY_WORKLOAD_TOOLS: readonly ChartCapabilityToolDef[] = [
  START_TABULAR_QUERY_WORKLOAD_TOOL,
  NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL,
  COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL,
];

export interface ExcelTabularQueryFeatureConfig {
  enabled: boolean;
  columnMaxInFlightBatches: number;
  cellMaxInFlightSingles: number;
}

/** Fatal workload execution that must suspend the run with a checkpoint. */
export class TabularQueryWorkloadSuspensionError extends Error {
  constructor(message: string, options?: { cause?: unknown }) {
    super(message);
    this.name = "TabularQueryWorkloadSuspensionError";
    if (options && "cause" in options) {
      (this as Error & { cause?: unknown }).cause = options.cause;
    }
  }
}

export interface TabularQueryWorkloadClient {
  resolveDataFormat(
    request: ResolveDataFormatRequest,
    signal?: AbortSignal,
    langfuseMetadata?: Record<string, unknown>,
  ): Promise<ResolveDataFormatResponse>;
  getFunctionCallMetadata(
    request: GetFunctionCallMetadataRequest,
    signal?: AbortSignal,
    langfuseMetadata?: Record<string, unknown>,
  ): Promise<GetFunctionCallMetadataResponse>;
  executeBatchQuery(
    request: ExecuteBatchQueryRequest,
    signal?: AbortSignal,
    langfuseMetadata?: Record<string, unknown>,
  ): Promise<ExecuteBatchQueryResponse>;
  executeCellBatchQuery?(
    request: ExecuteBatchQueryRequest,
    signal?: AbortSignal,
    langfuseMetadata?: Record<string, unknown>,
  ): Promise<ExecuteBatchQueryResponse>;
  executeSingleQuery(
    request: ExecuteSingleQueryRequest,
    signal?: AbortSignal,
    langfuseMetadata?: Record<string, unknown>,
  ): Promise<ExecuteQueryResult>;
}

export type TabularIdentityResolver = (
  companyLabel: string,
  signal?: AbortSignal,
) => Promise<FactSetTabularIdentityResolution>;

export interface TabularQueryWorkloadCompletionIssue {
  issue:
    | "tabular_query_workload_pending"
    | "tabular_query_workload_wave_uncommitted";
  workloadId: string;
  nextTool:
    | typeof NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME
    | typeof COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME;
  remainingRows: number;
  nextRowNumbers: number[];
  instruction: string;
}

interface ParsedA1Range {
  startColumn: number;
  endColumn: number;
  startRow: number;
  endRow: number;
}

interface CellSnapshot {
  value: string | number | boolean | null;
  formula: string | null;
}

interface SingleColumnSnapshotRead {
  snapshots?: Map<number, CellSnapshot>;
  evidenceSource?: ReadRangeContentResult["evidenceSource"];
  error?: string;
}

interface StoredIdentity {
  companyId?: string;
  companyTicker?: string;
  /**
   * The identity resolver returned a definite no-match and the run has selected
   * user files, so the row executes against its label plus those files alone.
   */
  nameOnly?: true;
}

type RowFailureCode =
  | "COMPANY_NOT_FOUND"
  | "COMPANY_LOOKUP_FAILED"
  | "QUERY_EXECUTION_FAILED"
  | "QUERY_EXECUTION_AMBIGUOUS"
  | "QUERY_RESULT_MISSING"
  | "QUERY_RESPONSE_FAILED"
  | "NO_DATA_RETURNED"
  | "INVALID_TYPED_RESULT"
  | "STALE_WORKBOOK_ROW"
  | "CELL_ROW_LIMIT_EXCEEDED";

type TabularRowStatus = "pending" | "active" | "closed";

interface TabularWorkloadRow {
  rowNumber: number;
  sourceCell: string;
  idCell?: string;
  outputCell: string;
  label: string;
  labelKey: string;
  sourceSnapshot: CellSnapshot;
  idSnapshot?: CellSnapshot;
  providedCompanyId?: string;
  identity?: StoredIdentity;
  status: TabularRowStatus;
  result?: ExecuteQueryResult;
  failureCode?: RowFailureCode;
  transientFailure?: boolean;
  writeAttempted?: boolean;
  verifiedUsable?: boolean;
  preparedWrite?: PreparedTabularWriteValue;
  committedValue?: string | number | boolean;
}

interface StoredExecutionOutcome {
  result?: ExecuteQueryResult;
  failureCode?: RowFailureCode;
  /**
   * The service answered with a server error, so it refused the row instead of
   * possibly running it. Only these failures may be executed again; a dropped
   * connection or a timeout may already have billed an execution.
   */
  transient?: boolean;
}

interface PreparedTabularWriteValue {
  value: string | number | boolean;
  numberFormat?: string;
}

interface TabularWriteMismatchDetail {
  rowNumber: number;
  expectedType: string;
  observedType: string;
  formulaPresent: boolean;
}

interface TabularWriteVerificationResult {
  complete: boolean;
  error?: string;
  mismatchDetails?: TabularWriteMismatchDetail[];
}

interface TabularWorkload {
  id: string;
  stepId: string;
  sheetName: string;
  companyAddress: string;
  companyIdAddress: string | null;
  outputAddress: string;
  companyRange: ParsedA1Range;
  companyIdRange: ParsedA1Range | null;
  outputRange: ParsedA1Range;
  prompt: string;
  responseType: TabularResponseType;
  queryOptions: TabularQueryOptions;
  mode: "column" | "cell";
  precomputedFunctionCalls: GetFunctionCallMetadataResponse["precomputedFunctionCalls"];
  /**
   * Company labels sent to one column execution call. A slow resolved function
   * can leave a full-size batch unanswered inside the upstream edge's read
   * timeout, so the workload narrows this after a wave the service could not
   * finish and keeps the smaller size for every later wave.
   */
  columnBatchSize: number;
  protectedCompanySnapshots: Map<number, CellSnapshot>;
  protectedCompanyIdSnapshots: Map<number, CellSnapshot> | null;
  rows: TabularWorkloadRow[];
  /**
   * Output rows that already held a value or formula when the workload was
   * registered. They carry no row, so the workload never executes or writes
   * them, and the output guards treat them as owned by the workbook.
   */
  prefilledOutputRows: number[];
  activeWaveRows: number[];
  identityCache: Map<string, FactSetTabularIdentityResolution>;
  executionCache: Map<string, StoredExecutionOutcome>;
  identitiesByLabel: Map<string, Set<string>>;
  inFlightExecutionKeys: Set<string>;
  waveInterrupted: boolean;
  blocked: boolean;
}

/**
 * JSON-safe continuation state for one tabular worksheet workload.
 *
 * Maps and sets are represented as tuples/arrays so this object can be stored
 * directly inside {@link ExecutorRunCheckpoint}.
 */
export interface TabularQueryWorkloadCheckpoint {
  version: 1;
  workloadSequence: number;
  progressVersion: number;
  verifiedUsableRows: number;
  researchScope: {
    dataSources: string[];
    userFileIds: string[];
  };
  /**
   * Settled date-system probes keyed by the normalized pinned-workbook name so
   * a resumed workload keeps one DATE representation for every row.
   */
  dateSystemProbes?: Array<[string, ExcelDateSystem | null]>;
  workload: {
    id: string;
    stepId: string;
    sheetName: string;
    companyAddress: string;
    companyIdAddress: string | null;
    outputAddress: string;
    companyRange: ParsedA1Range;
    companyIdRange: ParsedA1Range | null;
    outputRange: ParsedA1Range;
    prompt: string;
    responseType: TabularResponseType;
    queryOptions: TabularQueryOptions;
    mode: "column" | "cell";
    precomputedFunctionCalls: GetFunctionCallMetadataResponse["precomputedFunctionCalls"];
    columnBatchSize?: number;
    protectedCompanySnapshots: Array<[number, CellSnapshot]>;
    protectedCompanyIdSnapshots: Array<[number, CellSnapshot]> | null;
    rows: Array<{
      rowNumber: number;
      sourceCell: string;
      idCell?: string;
      outputCell: string;
      label: string;
      labelKey: string;
      sourceSnapshot: CellSnapshot;
      idSnapshot?: CellSnapshot;
      providedCompanyId?: string;
      identity?: StoredIdentity;
      status: TabularRowStatus;
      result?: ExecuteQueryResult;
      failureCode?: RowFailureCode;
      transientFailure?: boolean;
      writeAttempted?: boolean;
      verifiedUsable?: boolean;
      preparedWrite?: PreparedTabularWriteValue;
      committedValue?: string | number | boolean;
    }>;
    /** Absent in checkpoints written before pre-filled rows were skippable. */
    prefilledOutputRows?: number[];
    activeWaveRows: number[];
    identityCache: Array<[string, FactSetTabularIdentityResolution]>;
    executionCache: Array<[string, StoredExecutionOutcome]>;
    identitiesByLabel: Array<[string, string[]]>;
    inFlightExecutionKeys: string[];
    waveInterrupted: boolean;
    blocked: boolean;
  };
}

interface StartInput {
  stepId: string;
  sheetName: string;
  companyAddress: string;
  companyIdAddress: string | null;
  outputAddress: string;
  prompt: string;
  responseType: TabularResponseType;
  tagOptions: string[];
  noMatchValue?: string;
  dataFormat: TabularDataFormat;
}

interface ExecutionGroup {
  key: string;
  representative: TabularWorkloadRow;
  rows: TabularWorkloadRow[];
}

type ColumnExecutionTask =
  | { kind: "batch"; groups: ExecutionGroup[] }
  | { kind: "single"; group: ExecutionGroup };

const DEFAULT_CLIENT: TabularQueryWorkloadClient = {
  resolveDataFormat,
  getFunctionCallMetadata,
  executeBatchQuery,
  executeCellBatchQuery,
  executeSingleQuery,
};

function optionalLangfuseMetadata(
  metadata: Record<string, unknown> | undefined,
): [] | [Record<string, unknown>] {
  return metadata ? [metadata] : [];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function trimmedString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Returns true only when the user's own request asks for an exhaustive
 * worksheet list and does not also provide a finite row boundary.
 */
export function requestsAllPopulatedTabularRows(
  userRequest: string | undefined,
): boolean {
  const request = userRequest?.trim() ?? "";
  if (!request) return false;

  const hasExplicitFiniteBoundary =
    /(?:^|[^A-Z0-9_])\$?[A-Z]{1,3}\$?\d+\s*:\s*\$?[A-Z]{1,3}\$?\d+(?:$|[^A-Z0-9_])/i.test(
      request,
    ) ||
    /\brows?\s+\d+\s*(?:(?:through|thru|to)\s+(?:row\s+)?\d+|[-:]\s*\d+)\b/i.test(
      request,
    ) ||
    /\bfrom\s+row\s+\d+\s+(?:through|thru|to)\s+row\s+\d+\b/i.test(
      request,
    ) ||
    /\b(?:first|top)\s+\d+\s+(?:compan(?:y|ies)|company\s+rows?|rows?|entries)\b/i.test(
      request,
    );
  if (hasExplicitFiniteBoundary) return false;

  return (
    /\b(?:for\s+)?(?:each|every|all)\s+(?:(?:populated|non[-\s]?blank|existing|listed)\s+)*(?:compan(?:y|ies)|issuers?|entities|tickers?|rows?|entries)\b/i.test(
      request,
    ) ||
    /\b(?:entire|whole)\s+(?:company\s+)?(?:list|column|range)\b/i.test(
      request,
    )
  );
}

function parseA1Range(address: string): ParsedA1Range | null {
  if (!address || address.includes("!")) return null;
  const match = address
    .trim()
    .match(/^\$?([A-Z]+)\$?(\d+)(?::\$?([A-Z]+)\$?(\d+))?$/i);
  if (!match) return null;
  const toColumn = (letters: string): number =>
    letters
      .toUpperCase()
      .split("")
      .reduce(
        (total, character) =>
          total * 26 + character.charCodeAt(0) - 64,
        0,
      );
  const firstColumn = toColumn(match[1]);
  const secondColumn = toColumn(match[3] ?? match[1]);
  const firstRow = Number(match[2]);
  const secondRow = Number(match[4] ?? match[2]);
  if (
    !Number.isInteger(firstRow) ||
    !Number.isInteger(secondRow) ||
    firstRow < 1 ||
    secondRow < 1 ||
    firstRow > EXCEL_MAX_ROW ||
    secondRow > EXCEL_MAX_ROW ||
    firstColumn < 1 ||
    secondColumn < 1 ||
    firstColumn > EXCEL_MAX_COLUMN ||
    secondColumn > EXCEL_MAX_COLUMN ||
    firstColumn > secondColumn ||
    firstRow > secondRow
  ) {
    return null;
  }
  return {
    startColumn: firstColumn,
    endColumn: secondColumn,
    startRow: firstRow,
    endRow: secondRow,
  };
}

function columnName(columnNumber: number): string {
  let current = columnNumber;
  let result = "";
  while (current > 0) {
    current -= 1;
    result = String.fromCharCode(65 + (current % 26)) + result;
    current = Math.floor(current / 26);
  }
  return result;
}

function rowRangeAddress(
  range: ParsedA1Range,
  startRow: number,
  endRow: number,
): string {
  return `${columnName(range.startColumn)}${startRow}:${columnName(
    range.endColumn,
  )}${endRow}`;
}

function cellAddress(range: ParsedA1Range, rowNumber: number): string {
  return `${columnName(range.startColumn)}${rowNumber}`;
}

function rangesOverlap(left: ParsedA1Range, right: ParsedA1Range): boolean {
  return !(
    left.endColumn < right.startColumn ||
    right.endColumn < left.startColumn ||
    left.endRow < right.startRow ||
    right.endRow < left.startRow
  );
}

function normalizeLabel(value: string): string {
  return value
    .normalize("NFKC")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

function normalizeIdentity(value: string): string {
  return value.normalize("NFKC").trim().toLowerCase();
}

function normalizeFormula(value: unknown): string | null {
  return typeof value === "string" && value.trim()
    ? value.trim().replace(/^=/, "").replace(/\s+/g, " ").toUpperCase()
    : null;
}

function normalizeCellValue(
  value: unknown,
): string | number | boolean | null | undefined {
  if (value === null || value === undefined || value === "") return null;
  if (
    typeof value === "string" ||
    typeof value === "boolean" ||
    (typeof value === "number" && Number.isFinite(value))
  ) {
    return value;
  }
  return undefined;
}

function executableFormulaFromCellEvidence(
  value: CellSnapshot["value"],
  rawFormula: unknown,
): string | null {
  if (typeof rawFormula !== "string") return null;
  const formula = rawFormula.trim();
  if (!formula) return null;

  // The FactSet host's GetRangeInfo payload uses a field named `formula`
  // for both formulas and static cell contents. Static text is commonly
  // echoed unchanged in both `value` and `formula`; it is not executable
  // Excel content. A real Excel formula is returned with a leading "=".
  // The equality check also preserves literal text values such as "=N/A".
  if (typeof value === "string" && Object.is(value, formula)) return null;
  return formula.startsWith("=") ? formula : null;
}

function snapshotsEqual(left: CellSnapshot, right: CellSnapshot): boolean {
  return (
    Object.is(left.value, right.value) &&
    normalizeFormula(left.formula) === normalizeFormula(right.formula)
  );
}

function rangesEqual(left: ParsedA1Range, right: ParsedA1Range): boolean {
  return (
    left.startColumn === right.startColumn &&
    left.endColumn === right.endColumn &&
    left.startRow === right.startRow &&
    left.endRow === right.endRow
  );
}

function cloneJsonSafe<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function isBlankSnapshot(snapshot: CellSnapshot): boolean {
  return snapshot.value === null && snapshot.formula === null;
}

function protectedSnapshotsEqual(
  left: CellSnapshot,
  right: CellSnapshot,
): boolean {
  return Object.is(left.value, right.value) && Object.is(left.formula, right.formula);
}

function parseProtectedSnapshotEntries(
  rawEntries: unknown,
  range: ParsedA1Range,
  label: string,
): Map<number, CellSnapshot> {
  const expectedCount = range.endRow - range.startRow + 1;
  if (!Array.isArray(rawEntries) || rawEntries.length !== expectedCount) {
    throw new Error(
      `The tabular workload checkpoint does not contain a complete ${label} snapshot.`,
    );
  }
  const snapshots = new Map<number, CellSnapshot>();
  for (const rawEntry of rawEntries) {
    if (!Array.isArray(rawEntry) || rawEntry.length !== 2) {
      throw new Error(
        `The tabular workload checkpoint contains an invalid ${label} snapshot.`,
      );
    }
    const [rowNumber, rawSnapshot] = rawEntry;
    if (
      !Number.isInteger(rowNumber) ||
      (rowNumber as number) < range.startRow ||
      (rowNumber as number) > range.endRow ||
      snapshots.has(rowNumber as number) ||
      !isRecord(rawSnapshot)
    ) {
      throw new Error(
        `The tabular workload checkpoint contains an invalid ${label} snapshot.`,
      );
    }
    const value = normalizeCellValue(rawSnapshot.value);
    const rawFormula = rawSnapshot.formula;
    if (
      value === undefined ||
      (rawFormula !== null && typeof rawFormula !== "string")
    ) {
      throw new Error(
        `The tabular workload checkpoint contains an invalid ${label} snapshot.`,
      );
    }
    snapshots.set(rowNumber as number, {
      value,
      formula: rawFormula as string | null,
    });
  }
  return snapshots;
}

function parseDataFormat(
  rawValue: unknown,
): { value?: TabularDataFormat; error?: string } {
  if (!isRecord(rawValue)) return { error: "dataFormat must be an object." };
  const unknownKey = Object.keys(rawValue).find(
    (key) => !DATA_FORMAT_KEYS.has(key),
  );
  if (unknownKey) {
    return { error: `dataFormat.${unknownKey} is not supported.` };
  }
  const missingKey = Array.from(DATA_FORMAT_KEYS).find(
    (key) => !Object.prototype.hasOwnProperty.call(rawValue, key),
  );
  if (missingKey) {
    return { error: `dataFormat.${missingKey} is required.` };
  }
  const formatType = trimmedString(rawValue.formatType);
  const optionalString = (field: string): string | undefined => {
    const value = rawValue[field];
    return typeof value === "string" && value.trim()
      ? value.trim()
      : undefined;
  };
  const rawDecimalPlaces = rawValue.decimalPlaces;
  const decimalPlaces =
    rawDecimalPlaces === null || rawDecimalPlaces === undefined
      ? undefined
      : Number.isInteger(rawDecimalPlaces) &&
          (rawDecimalPlaces as number) >= 0 &&
          (rawDecimalPlaces as number) <= 6
        ? (rawDecimalPlaces as number)
        : null;
  if (decimalPlaces === null) {
    return { error: "dataFormat.decimalPlaces must be an integer from 0 to 6 or null." };
  }
  switch (formatType) {
    case "auto":
      return { value: { formatType: "auto" } };
    case "number":
      return {
        value: {
          formatType: "number",
          ...(optionalString("unit") ? { unit: optionalString("unit") } : {}),
          ...(decimalPlaces !== undefined ? { decimalPlaces } : {}),
        },
      };
    case "currency":
      return {
        value: {
          formatType: "currency",
          ...(optionalString("currency")
            ? { currency: optionalString("currency") }
            : {}),
          ...(optionalString("unit") ? { unit: optionalString("unit") } : {}),
          ...(decimalPlaces !== undefined ? { decimalPlaces } : {}),
        },
      };
    case "percentage":
      return {
        value: {
          formatType: "percentage",
          ...(decimalPlaces !== undefined ? { decimalPlaces } : {}),
        },
      };
    case "date":
      return {
        value: {
          formatType: "date",
          ...(optionalString("dateFormat")
            ? { dateFormat: optionalString("dateFormat") }
            : {}),
        },
      };
    case "template": {
      const template = optionalString("template");
      return template
        ? { value: { formatType: "template", template } }
        : { error: "dataFormat.template is required for template format." };
    }
    default:
      return { error: "dataFormat.formatType is not supported." };
  }
}

function parseStartInput(rawInput: unknown): {
  value?: StartInput;
  error?: string;
} {
  if (!isRecord(rawInput)) return { error: "Input must be an object." };
  const unknownKey = Object.keys(rawInput).find(
    (key) => !START_INPUT_KEYS.has(key),
  );
  if (unknownKey) return { error: `${unknownKey} is not supported.` };
  const missingKey = Array.from(START_INPUT_KEYS).find(
    (key) => !Object.prototype.hasOwnProperty.call(rawInput, key),
  );
  if (missingKey) return { error: `${missingKey} is required.` };

  const stepId = trimmedString(rawInput.stepId);
  const sheetName = trimmedString(rawInput.sheetName);
  const companyAddress = trimmedString(rawInput.companyAddress);
  const outputAddress = trimmedString(rawInput.outputAddress);
  const prompt = trimmedString(rawInput.prompt);
  const companyIdAddress =
    rawInput.companyIdAddress === null
      ? null
      : typeof rawInput.companyIdAddress === "string" &&
          rawInput.companyIdAddress.trim()
        ? rawInput.companyIdAddress.trim()
        : undefined;
  if (
    !stepId ||
    !sheetName ||
    !companyAddress ||
    !outputAddress ||
    !prompt
  ) {
    return {
      error:
        "stepId, sheetName, companyAddress, outputAddress, and prompt are required.",
    };
  }
  if (companyIdAddress === undefined) {
    return {
      error: "companyIdAddress must be a non-empty string or null.",
    };
  }
  if (prompt.length > 32_000) {
    return { error: "prompt exceeds the 32,000-character limit." };
  }
  if (
    typeof rawInput.responseType !== "string" ||
    !RESPONSE_TYPES.has(rawInput.responseType as TabularResponseType)
  ) {
    return { error: "responseType is not supported." };
  }
  const responseType = rawInput.responseType as TabularResponseType;
  if (!Array.isArray(rawInput.tagOptions)) {
    return { error: "tagOptions must be an array." };
  }
  const tagOptions = rawInput.tagOptions.map(trimmedString);
  if (
    tagOptions.some((value) => !value) ||
    tagOptions.length > 100 ||
    new Set(tagOptions).size !== tagOptions.length
  ) {
    return {
      error:
        "tagOptions must contain at most 100 unique non-empty strings.",
    };
  }
  const isSelect =
    responseType === "SINGLE_SELECT" || responseType === "MULTI_SELECT";
  if (isSelect !== (tagOptions.length > 0)) {
    return {
      error:
        "tagOptions are required only for SINGLE_SELECT and MULTI_SELECT responses.",
    };
  }
  const noMatchValue =
    rawInput.noMatchValue === null || rawInput.noMatchValue === undefined
      ? undefined
      : typeof rawInput.noMatchValue === "string"
        ? rawInput.noMatchValue.trim()
        : null;
  if (noMatchValue === null || (noMatchValue?.length ?? 0) > 1_000) {
    return {
      error: "noMatchValue must be a string of at most 1,000 characters or null.",
    };
  }
  const dataFormat = parseDataFormat(rawInput.dataFormat);
  if (!dataFormat.value) return { error: dataFormat.error };

  return {
    value: {
      stepId,
      sheetName,
      companyAddress,
      companyIdAddress,
      outputAddress,
      prompt,
      responseType,
      tagOptions,
      ...(noMatchValue !== undefined ? { noMatchValue } : {}),
      dataFormat: dataFormat.value,
    },
  };
}

async function readSingleColumnSnapshotsOnce(
  ctx: ExecutorToolRuntimeContext,
  sheetName: string,
  address: string,
  range: ParsedA1Range,
  options: { allowCsvValueOnlyEvidence?: boolean } = {},
): Promise<SingleColumnSnapshotRead> {
  let readResult: Awaited<ReturnType<typeof readRangeContent>>;
  try {
    readResult = await readRangeContent(
      sheetName,
      address,
      {
        includeFormulas: true,
        includeFormats: false,
        includeEmptyCells: true,
      },
      ctx.session.pinnedWorkbookName,
    );
  } catch {
    return { error: `${sheetName}!${address} could not be read safely.` };
  }
  const readRange = readResult.range;
  if (
    readResult.truncated ||
    !readRange ||
    !Array.isArray(readRange.values)
  ) {
    return {
      error:
        `${sheetName}!${address} did not return complete dense value and formula matrices.`,
    };
  }
  const formulaRows = Array.isArray(readRange.formulas)
    ? readRange.formulas
    : options.allowCsvValueOnlyEvidence &&
        readResult.evidenceSource === "csv_fallback"
      ? readRange.values.map((valueRow) =>
          Array.from({ length: valueRow.length }, () => null),
        )
    : readRange.values.every(
          (valueRow) =>
            Array.isArray(valueRow) &&
            valueRow.every((value) => normalizeCellValue(value) === null),
        )
      ? readRange.values.map((valueRow) =>
          Array.from({ length: valueRow.length }, () => null),
        )
      : null;
  if (!formulaRows) {
    return {
      error:
        `${sheetName}!${address} did not return complete dense value and formula matrices.`,
    };
  }
  const snapshots = new Map<number, CellSnapshot>();
  for (let rowNumber = range.startRow; rowNumber <= range.endRow; rowNumber += 1) {
    const rowIndex = rowNumber - 1 - readRange.startRow;
    const columnIndex = range.startColumn - 1 - readRange.startColumn;
    const valueRow = readRange.values[rowIndex];
    const formulaRow = formulaRows[rowIndex];
    if (
      !Array.isArray(valueRow) ||
      !Array.isArray(formulaRow) ||
      !(columnIndex in valueRow) ||
      !(columnIndex in formulaRow)
    ) {
      return {
        error: `${sheetName}!${address} did not cover every requested coordinate.`,
      };
    }
    const value = normalizeCellValue(valueRow[columnIndex]);
    if (value === undefined) {
      return {
        error: `${sheetName}!${cellAddress(range, rowNumber)} contains an unsupported value.`,
      };
    }
    snapshots.set(rowNumber, {
      value,
      formula: executableFormulaFromCellEvidence(
        value,
        formulaRow[columnIndex],
      ),
    });
  }
  return {
    snapshots,
    evidenceSource: readResult.evidenceSource,
  };
}

async function readSingleColumnSnapshots(
  ctx: ExecutorToolRuntimeContext,
  sheetName: string,
  address: string,
  range: ParsedA1Range,
  options: { allowCsvValueOnlyEvidence?: boolean } = {},
): Promise<SingleColumnSnapshotRead> {
  let lastRead: SingleColumnSnapshotRead = {
    error: `${sheetName}!${address} could not be read safely.`,
  };
  for (const delayMs of TABULAR_HOST_READ_RETRY_DELAYS_MS) {
    await waitForTabularDelay(delayMs, ctx.signal);
    lastRead = await readSingleColumnSnapshotsOnce(
      ctx,
      sheetName,
      address,
      range,
      options,
    );
    if (lastRead.snapshots) return lastRead;
  }
  return lastRead;
}

function usedRangeEndRow(sheet: WorksheetMetadata): number | null {
  const address = sheet.usedRange?.address.split("!").pop();
  const parsed = address ? parseA1Range(address) : null;
  if (parsed) return parsed.endRow;
  const fallback = sheet.usedRange?.rowCount;
  return typeof fallback === "number" &&
    Number.isInteger(fallback) &&
    fallback >= 1 &&
    fallback <= EXCEL_MAX_ROW
    ? fallback
    : null;
}

async function resolveAllPopulatedTabularEndRow(
  ctx: ExecutorToolRuntimeContext,
  sheetName: string,
  companyRange: ParsedA1Range,
): Promise<{ endRow?: number; error?: string }> {
  let sheets: WorksheetMetadata[];
  try {
    sheets = await listWorksheetsMetadata(ctx.session.pinnedWorkbookName);
  } catch {
    return {
      error:
        `The complete populated scope for worksheet "${sheetName}" could not be determined.`,
    };
  }
  throwIfAborted(ctx.signal);

  const normalizedSheetName = sheetName.trim().toLowerCase();
  const sheet = sheets.find(
    (candidate) => candidate.name.trim().toLowerCase() === normalizedSheetName,
  );
  const upperRow = sheet ? usedRangeEndRow(sheet) : null;
  if (!sheet || upperRow === null) {
    return {
      error:
        `The complete populated scope for worksheet "${sheetName}" is unavailable; ` +
        "the workload was not registered.",
    };
  }
  if (upperRow < companyRange.startRow) {
    return { endRow: companyRange.startRow };
  }

  for (
    let pageEnd = upperRow;
    pageEnd >= companyRange.startRow;
    pageEnd -= TABULAR_SCOPE_SCAN_PAGE_ROWS
  ) {
    throwIfAborted(ctx.signal);
    const pageStart = Math.max(
      companyRange.startRow,
      pageEnd - TABULAR_SCOPE_SCAN_PAGE_ROWS + 1,
    );
    const pageRange: ParsedA1Range = {
      ...companyRange,
      startRow: pageStart,
      endRow: pageEnd,
    };
    const address = rowRangeAddress(pageRange, pageStart, pageEnd);
    const read = await readSingleColumnSnapshots(
      ctx,
      sheetName,
      address,
      pageRange,
      { allowCsvValueOnlyEvidence: true },
    );
    if (read.error || !read.snapshots) {
      return {
        error:
          read.error ??
          `The complete company column on worksheet "${sheetName}" could not be scanned safely.`,
      };
    }
    for (let rowNumber = pageEnd; rowNumber >= pageStart; rowNumber -= 1) {
      const value = read.snapshots.get(rowNumber)?.value;
      if (
        value !== null &&
        value !== undefined &&
        String(value).trim().length > 0
      ) {
        return { endRow: rowNumber };
      }
    }
  }

  return { endRow: companyRange.startRow };
}

function trustedResearchScope(
  researchContext: ToolCallContext["researchContext"] | undefined,
): { dataSources: string[]; userFileIds: string[] } {
  const dataSources = Array.from(
    new Set(
      (researchContext?.dataSources ?? [])
        .map((value) => (typeof value === "string" ? value.trim() : ""))
        .filter(Boolean),
    ),
  );
  const userFileIds = Array.from(
    new Set(
      (researchContext?.userFileIds ?? [])
        .filter((value): value is string => typeof value === "string")
        .map((value) => value.trim())
        .filter(Boolean),
    ),
  );
  if (userFileIds.length > 0) {
    const withoutUserFiles = dataSources.filter(
      (value) => value.toUpperCase() !== USER_FILES_DATA_SOURCE,
    );
    return {
      dataSources: [USER_FILES_DATA_SOURCE, ...withoutUserFiles],
      userFileIds,
    };
  }
  return { dataSources, userFileIds };
}

function routedTabularDataSources(
  prompt: string,
  dataSources: readonly string[],
): string[] {
  if (!CURRENT_CEO_PROMPT_PATTERN.test(prompt)) return [...dataSources];

  // A column resolves to a single retrieval function that then answers every
  // row, so an unnarrowed selection lets it settle on a source that covers only
  // part of a long list: the company-website client leaves most rows with no
  // leadership page to cite. Name the source that carries the question instead,
  // preferring the People client and falling back to open web retrieval, and
  // preserve explicitly selected user files.
  const available = new Set(dataSources.map((source) => source.toUpperCase()));
  const routedSource = available.has(FACTSET_PEOPLE_DATA_SOURCE)
    ? FACTSET_PEOPLE_DATA_SOURCE
    : available.has(WEB_SEARCH_DATA_SOURCE)
      ? WEB_SEARCH_DATA_SOURCE
      : null;
  if (!routedSource) return [...dataSources];

  return dataSources.filter((source) => {
    const normalized = source.toUpperCase();
    return (
      normalized === routedSource || normalized === USER_FILES_DATA_SOURCE
    );
  });
}

/**
 * The cross-company filings search answers questions that name no company by
 * sweeping the whole universe. A column prompt names no company either, because
 * the company arrives per row, so the selector reads an ordinary per-row
 * question as a universe sweep and every row comes back with no citable
 * evidence.
 */
function resolvesOnlyToCrossCompanySearch(
  functionCalls: readonly { functionName: string }[],
): boolean {
  return (
    functionCalls.length > 0 &&
    functionCalls.every(
      (call) => call.functionName === CROSS_COMPANY_SEARCH_FUNCTION,
    )
  );
}

/**
 * Narrows a selection to the one source that answers a question about a named
 * company whatever the question is, so a re-resolve has somewhere to land.
 * Returns null when the selection is already that narrow or carries no web
 * retrieval to fall back on.
 */
function perCompanyRetrievalFallback(
  dataSources: readonly string[],
): string[] | null {
  const kept = dataSources.filter((source) => {
    const normalized = source.toUpperCase();
    return (
      normalized === WEB_SEARCH_DATA_SOURCE ||
      normalized === USER_FILES_DATA_SOURCE
    );
  });
  if (
    kept.length === dataSources.length ||
    !kept.some((source) => source.toUpperCase() === WEB_SEARCH_DATA_SOURCE)
  ) {
    return null;
  }
  return kept;
}

/**
 * A column that resolved to the structured FactSet client *and* to per-document
 * retrieval executes cell by cell, spending a search per row on a figure one
 * batched structured request already carries. Narrowing to the structured
 * sources gives the resolver a chance to return that batched column plan.
 * Returns null when the selection cannot narrow any further, leaving the
 * original resolution untouched.
 */
function structuredFactSetRetryDataSources(
  dataSources: readonly string[],
  functionCalls: readonly { functionName: string }[],
): string[] | null {
  const functionNames = new Set(functionCalls.map((call) => call.functionName));
  if (
    !functionNames.has(FACTSET_STRUCTURED_DATA_FUNCTION) ||
    functionNames.size < 2
  ) {
    return null;
  }
  const kept = dataSources.filter((source) =>
    FACTSET_STRUCTURED_DATA_SOURCES.has(source.toUpperCase()),
  );
  return kept.length > 0 && kept.length < dataSources.length ? kept : null;
}

/**
 * True when the selection carries a source the structured FactSet client answers
 * in batched column mode. A metric column with one of these selected belongs to
 * this workload, which fills sixty rows per wave from batched structured
 * requests, rather than to the per-row retrieval a metric would otherwise get.
 */
export function hasStructuredFactSetDataSource(
  dataSources: readonly string[] | null | undefined,
): boolean {
  return (
    dataSources?.some((source) =>
      FACTSET_STRUCTURED_DATA_SOURCES.has(source.toUpperCase()),
    ) === true
  );
}

function targetForRow(
  row: Pick<TabularWorkloadRow, "label" | "identity" | "providedCompanyId">,
  userFileIds: readonly string[],
): TabularCompanyTarget {
  const companyId = row.identity?.companyId ?? row.providedCompanyId;
  return {
    company: {
      companyName: row.label,
      ...(companyId ? { companyId } : {}),
      ...(row.identity?.companyTicker
        ? { companyTicker: row.identity.companyTicker }
        : {}),
      ...(userFileIds.length > 0
        ? { userFileIds: [...userFileIds] }
        : {}),
    },
  };
}

function safeErrorMessage(error: unknown): string {
  return error instanceof TabularQueryExecutionError
    ? error.message
    : "Tabular query preflight failed.";
}

function isFatalExecutionFailure(error: unknown): boolean {
  return (
    !(error instanceof TabularQueryExecutionError) ||
    error.kind === "auth" ||
    error.kind === "rate_limit" ||
    error.kind === "validation" ||
    error.kind === "contract" ||
    error.kind === "disabled"
  );
}

function waveExhaustedByServiceFailures(
  waveRows: readonly TabularWorkloadRow[],
): boolean {
  const executedRows = waveRows.filter((row) => row.identity).length;
  if (executedRows === 0) return false;
  const serviceFailedRows = waveRows.filter(
    (row) => row.failureCode === "QUERY_EXECUTION_FAILED",
  ).length;
  return (
    serviceFailedRows >=
    executedRows * TABULAR_WAVE_SERVICE_FAILURE_STOP_RATIO
  );
}

/**
 * A server error is the one failure the service reports without having run the
 * row, so executing it again cannot bill a second execution and can succeed
 * once the service is back.
 */
function isTransientServiceFailure(error: unknown): boolean {
  return (
    error instanceof TabularQueryExecutionError && error.kind === "upstream"
  );
}

/**
 * Executing an exhausted wave again is only worth a pause when the service
 * failed the rows without judging them. A wave the service refused on its
 * merits, or one that failed ambiguously in flight, fails or double-bills the
 * same way after any wait.
 */
function waveOutageLooksTransient(
  waveRows: readonly TabularWorkloadRow[],
): boolean {
  const failedRows = waveRows.filter(
    (row) => row.failureCode === "QUERY_EXECUTION_FAILED",
  );
  if (failedRows.length === 0) return false;
  return (
    failedRows.filter((row) => row.transientFailure).length >=
    failedRows.length * TABULAR_WAVE_SERVICE_FAILURE_STOP_RATIO
  );
}

function serviceUnavailableNextAction(pendingRows: number): string {
  return (
    `The tabular query service kept rejecting this wave across ` +
    `${TABULAR_SERVICE_OUTAGE_RETRY_DELAYS_MS.length} retries, so the workload ` +
    `stopped with ${pendingRows} row(s) still pending. Those rows are still ` +
    `blank; report that they need a rerun once the service recovers.`
  );
}

function isSafelyRejectedExecutionFailure(error: unknown): boolean {
  return (
    error instanceof TabularQueryExecutionError &&
    (error.kind === "auth" ||
      error.kind === "rate_limit" ||
      error.kind === "validation" ||
      error.kind === "disabled")
  );
}

function abortInFlightOnUnsafeFailure(
  controller: AbortController,
  error: unknown,
): void {
  if (!isSafelyRejectedExecutionFailure(error)) {
    controller.abort();
  }
}

/**
 * A resolver reports expected lookup problems through its resolution status, so
 * these error types can only mean the add-in itself is broken. Reporting them as
 * `COMPANY_LOOKUP_FAILED` would bill an execution wave against a bug and hide it
 * behind a plausible-looking per-row failure.
 */
function isProgrammingError(error: unknown): boolean {
  return (
    error instanceof TypeError ||
    error instanceof RangeError ||
    error instanceof ReferenceError ||
    error instanceof SyntaxError
  );
}

function isAbortFailure(error: unknown, signal?: AbortSignal): boolean {
  return (
    signal?.aborted === true ||
    (typeof DOMException === "function" &&
      error instanceof DOMException &&
      error.name === "AbortError") ||
    (error instanceof Error && error.name === "AbortError") ||
    (error instanceof TabularQueryExecutionError && error.kind === "aborted")
  );
}

function tabularAbortError(): Error {
  if (typeof DOMException === "function") {
    return new DOMException("Tabular query workload cancelled.", "AbortError");
  }
  const error = new Error("Tabular query workload cancelled.");
  error.name = "AbortError";
  return error;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw tabularAbortError();
}

async function waitForTabularDelay(
  delayMs: number,
  signal?: AbortSignal,
): Promise<void> {
  throwIfAborted(signal);
  if (delayMs <= 0) return;
  await new Promise<void>((resolve, reject) => {
    const onAbort = (): void => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
      reject(tabularAbortError());
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, delayMs);
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

function executionIdentityKey(row: TabularWorkloadRow): string {
  const identity =
    row.identity?.companyId ??
    row.providedCompanyId ??
    row.identity?.companyTicker ??
    "";
  return `${row.labelKey}\u0000${normalizeIdentity(identity)}`;
}

function resultOutcome(
  responseType: TabularResponseType,
  result: ExecuteQueryResult,
): StoredExecutionOutcome {
  if (result.responseShort.trim() === TABULAR_QUERY_FAILURE_SENTINEL) {
    return { failureCode: "QUERY_RESPONSE_FAILED" };
  }
  const typed = typedResultValue(responseType, result);
  if (typed !== undefined) return { result };
  // A row the service grounded in nothing and returned no typed value for is a
  // row it has no data for, not one whose value arrived in the wrong shape.
  // Both leave the cell blank, but only the latter can be helped by rewording
  // the prompt, so collapsing them sends the model back to retry a metric the
  // service will never answer.
  const returnedNoValue =
    result.typedValue === undefined || result.typedValue === null;
  const grounded = (result.citationIds?.length ?? 0) > 0;
  return returnedNoValue && !grounded && !result.sortValue
    ? { failureCode: "NO_DATA_RETURNED" }
    : { failureCode: "INVALID_TYPED_RESULT" };
}

function partition<T>(items: readonly T[], size: number): T[][] {
  const groups: T[][] = [];
  for (let index = 0; index < items.length; index += size) {
    groups.push(items.slice(index, index + size));
  }
  return groups;
}

function groupConsecutiveRows<T extends { rowNumber: number }>(
  rows: readonly T[],
  groupKey?: (row: T) => string,
): T[][] {
  const groups: T[][] = [];
  for (const row of [...rows].sort((a, b) => a.rowNumber - b.rowNumber)) {
    const current = groups.at(-1);
    if (
      !current ||
      row.rowNumber !== current[current.length - 1].rowNumber + 1 ||
      (groupKey &&
        groupKey(row) !== groupKey(current[current.length - 1]))
    ) {
      groups.push([row]);
    } else {
      current.push(row);
    }
  }
  return groups;
}

function quotedFormatLiteral(value: string): string {
  return `"${value.replace(/"/g, '""')}"`;
}

function decimalPattern(decimalPlaces: number): string {
  return decimalPlaces > 0
    ? `#,##0.${"0".repeat(decimalPlaces)}`
    : "#,##0";
}

function zeroPattern(decimalPlaces: number): string {
  return decimalPlaces > 0 ? `0.${"0".repeat(decimalPlaces)}` : "0";
}

function scaledUnitSuffix(unit: string | undefined): string {
  const normalized = unit?.trim();
  if (!normalized) return "";
  const scaledUnits: Record<string, string> = {
    k: "K",
    thousand: "K",
    thousands: "K",
    m: "M",
    million: "M",
    millions: "M",
    b: "B",
    billion: "B",
    billions: "B",
    t: "T",
    trillion: "T",
    trillions: "T",
  };
  return quotedFormatLiteral(
    scaledUnits[normalized.toLowerCase()] ?? normalized,
  );
}

function currencyPrefix(currency: string | undefined): string {
  const normalized = currency?.trim();
  if (!normalized) return "";
  const symbols: Record<string, string> = {
    USD: "$",
    EUR: "€",
    GBP: "£",
    JPY: "¥",
    CNY: "¥",
  };
  return quotedFormatLiteral(symbols[normalized.toUpperCase()] ?? `${normalized} `);
}

function threeSectionNumberFormat(
  decimalPlaces: number,
  prefix: string,
  suffix: string,
): string {
  const positive = `${prefix}${decimalPattern(decimalPlaces)}${suffix}`;
  const zero = `${prefix}${zeroPattern(decimalPlaces)}${suffix}`;
  return `${positive};(${positive});${zero}`;
}

/** Excel number format for an already-scaled tabular value. */
export function tabularNumberFormat(
  dataFormat: TabularDataFormat,
  responseType: TabularResponseType,
  numericDate: boolean,
): string | undefined {
  if (responseType === "DATE") {
    if (!numericDate) return undefined;
    return dataFormat.formatType === "date"
      ? dataFormat.dateFormat?.trim() || "yyyy-mm-dd"
      : undefined;
  }
  if (responseType !== "NUMBER") return undefined;
  const decimalPlaces =
    "decimalPlaces" in dataFormat && dataFormat.decimalPlaces !== undefined
      ? dataFormat.decimalPlaces
      : 0;
  switch (dataFormat.formatType) {
    case "number":
      return threeSectionNumberFormat(
        decimalPlaces,
        "",
        scaledUnitSuffix(dataFormat.unit),
      );
    case "currency":
      return threeSectionNumberFormat(
        decimalPlaces,
        currencyPrefix(dataFormat.currency),
        scaledUnitSuffix(dataFormat.unit),
      );
    case "percentage":
      return threeSectionNumberFormat(
        decimalPlaces,
        "",
        quotedFormatLiteral("%"),
      );
    default:
      return undefined;
  }
}

function visibleResponseText(value: string): string {
  return visibleCitationText(value)
    .replace(/\[\^[^\]]+\]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function typedResultValue(
  responseType: TabularResponseType,
  result: ExecuteQueryResult,
): string | number | boolean | undefined {
  switch (responseType) {
    case "NUMBER": {
      const sortValue = result.sortValue;
      const value =
        sortValue && "numberValue" in sortValue
          ? sortValue.numberValue
          : result.typedValue;
      return typeof value === "number" && Number.isFinite(value)
        ? value
        : undefined;
    }
    case "DATE":
      if (result.sortValue && "isoDatetimeValue" in result.sortValue) {
        return result.sortValue.isoDatetimeValue || undefined;
      }
      return typeof result.typedValue === "string" && result.typedValue
        ? result.typedValue
        : undefined;
    case "BOOLEAN":
      if (typeof result.typedValue === "boolean") return result.typedValue;
      switch (visibleResponseText(result.responseShort).toLowerCase()) {
        case "true":
          return true;
        case "false":
          return false;
        default:
          return undefined;
      }
    case "SINGLE_SELECT":
      return typeof result.typedValue === "string" && result.typedValue
        ? visibleResponseText(result.typedValue) || undefined
        : visibleResponseText(result.responseShort) || undefined;
    case "MULTI_SELECT":
      if (
        Array.isArray(result.typedValue) &&
        result.typedValue.length > 0 &&
        result.typedValue.every((value) => typeof value === "string")
      ) {
        return result.typedValue.join(", ");
      }
      return typeof result.typedValue === "string" && result.typedValue
        ? visibleResponseText(result.typedValue) || undefined
        : visibleResponseText(result.responseShort) || undefined;
    case "TEXT":
      return typeof result.typedValue === "string" && result.typedValue
        ? visibleResponseText(result.typedValue) || undefined
        : visibleResponseText(result.responseShort) || undefined;
  }
}

function prepareTypedWriteValue(
  responseType: TabularResponseType,
  result: ExecuteQueryResult,
  dataFormat: TabularDataFormat,
  dateSystem: ExcelDateSystem | null,
): PreparedTabularWriteValue | undefined {
  const rawValue = typedResultValue(responseType, result);
  if (rawValue === undefined) return undefined;
  if (responseType !== "DATE" || typeof rawValue !== "string") {
    return {
      value: rawValue,
      ...(tabularNumberFormat(dataFormat, responseType, false)
        ? {
            numberFormat: tabularNumberFormat(
              dataFormat,
              responseType,
              false,
            ),
          }
        : {}),
    };
  }

  const parsed = parseStrictIsoDateTime(rawValue);
  if (!parsed || !dateSystem) {
    return {
      value: parsed?.canonicalText ?? rawValue.trim(),
    };
  }
  const converted = excelSerialFromIso(rawValue, dateSystem);
  if (!converted) return { value: rawValue.trim() };
  return {
    value: converted.serial,
    ...(tabularNumberFormat(dataFormat, responseType, true)
      ? {
          numberFormat: tabularNumberFormat(
            dataFormat,
            responseType,
            true,
          ),
        }
      : {}),
  };
}

function outputMatchesExpected(
  snapshot: CellSnapshot,
  expected: string | number | boolean,
): boolean {
  return snapshot.formula === null && Object.is(snapshot.value, expected);
}

function tabularValueType(value: CellSnapshot["value"]): string {
  return value === null ? "null" : typeof value;
}

function tabularWriteMismatchDetail(
  rowNumber: number,
  snapshot: CellSnapshot,
  expected: string | number | boolean,
): TabularWriteMismatchDetail {
  return {
    rowNumber,
    expectedType: typeof expected,
    observedType: tabularValueType(snapshot.value),
    formulaPresent: snapshot.formula !== null,
  };
}

function diagnosticValuePreview(value: CellSnapshot["value"]): unknown {
  return typeof value === "string" && value.length > 120
    ? `${value.slice(0, 120)}…`
    : value;
}

async function verifyTabularWriteGroup(
  ctx: ExecutorToolRuntimeContext,
  sheetName: string,
  outputRange: ParsedA1Range,
  rows: readonly TabularWorkloadRow[],
  preparedByRow: ReadonlyMap<number, PreparedTabularWriteValue>,
  signal?: AbortSignal,
): Promise<TabularWriteVerificationResult> {
  const firstRow = rows[0]?.rowNumber;
  const lastRow = rows.at(-1)?.rowNumber;
  if (firstRow === undefined || lastRow === undefined) {
    return { complete: true };
  }
  const range = {
    ...outputRange,
    startRow: firstRow,
    endRow: lastRow,
  };
  const address = rowRangeAddress(outputRange, firstRow, lastRow);
  let lastError = `${sheetName}!${address} did not verify.`;
  let lastMismatchDetails: TabularWriteMismatchDetail[] | undefined;

  for (
    let attempt = 0;
    attempt < TABULAR_WRITE_VERIFY_DELAYS_MS.length;
    attempt += 1
  ) {
    await waitForTabularDelay(
      TABULAR_WRITE_VERIFY_DELAYS_MS[attempt],
      signal,
    );
    const readback = await readSingleColumnSnapshots(
      ctx,
      sheetName,
      address,
      range,
    );
    throwIfAborted(signal);
    if (readback.error || !readback.snapshots) {
      lastError =
        readback.error ?? `${sheetName}!${address} could not be verified safely.`;
      diag("write", "tabular_readback_incomplete", {
        sheetName,
        address,
        attempt: attempt + 1,
        error: lastError,
      });
      continue;
    }
    if (readback.evidenceSource !== "rich") {
      lastError =
        `${sheetName}!${address} did not return rich formula-aware read-back evidence.`;
      diag("write", "tabular_readback_untrusted_source", {
        sheetName,
        address,
        attempt: attempt + 1,
        evidenceSource: readback.evidenceSource ?? "unknown",
      });
      continue;
    }

    const mismatchDetails: TabularWriteMismatchDetail[] = [];
    const diagnosticMismatches: Array<Record<string, unknown>> = [];
    let coordinateMissing = false;
    for (const row of rows) {
      const snapshot = readback.snapshots.get(row.rowNumber);
      const prepared = preparedByRow.get(row.rowNumber);
      if (!snapshot || !prepared) {
        coordinateMissing = true;
        continue;
      }
      if (outputMatchesExpected(snapshot, prepared.value)) continue;
      mismatchDetails.push(
        tabularWriteMismatchDetail(row.rowNumber, snapshot, prepared.value),
      );
      diagnosticMismatches.push({
        rowNumber: row.rowNumber,
        expectedType: typeof prepared.value,
        observedType: tabularValueType(snapshot.value),
        formulaPresent: snapshot.formula !== null,
        expectedPreview: diagnosticValuePreview(prepared.value),
        observedPreview: diagnosticValuePreview(snapshot.value),
        formulaPreview: snapshot.formula
          ? diagnosticValuePreview(snapshot.formula)
          : null,
      });
    }
    if (!coordinateMissing && mismatchDetails.length === 0) {
      return { complete: true };
    }
    if (coordinateMissing) {
      lastError =
        `${sheetName}!${address} did not cover every requested coordinate.`;
    }
    if (mismatchDetails.length > 0) {
      lastMismatchDetails = mismatchDetails;
      const mismatchCells = mismatchDetails
        .slice(0, 8)
        .map((detail) => cellAddress(outputRange, detail.rowNumber));
      lastError =
        `${sheetName}!${address} is missing or mismatched at ` +
        `${mismatchCells.join(", ")}` +
        (mismatchDetails.length > mismatchCells.length
          ? ` and ${mismatchDetails.length - mismatchCells.length} more`
          : "");
      diag("write", "tabular_readback_mismatch", {
        sheetName,
        address,
        attempt: attempt + 1,
        mismatches: diagnosticMismatches.slice(0, 8),
      });
    }
  }

  return {
    complete: false,
    error: lastError,
    ...(lastMismatchDetails
      ? { mismatchDetails: lastMismatchDetails }
      : {}),
  };
}

function parseToolOutput(output: string): Record<string, unknown> | null {
  try {
    const parsed = JSON.parse(output) as unknown;
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function parseWorkloadInput(rawInput: unknown): {
  workloadId?: string;
  error?: string;
} {
  if (!isRecord(rawInput)) return { error: "Input must be an object." };
  const keys = Object.keys(rawInput);
  if (keys.length !== 1 || keys[0] !== "workloadId") {
    return {
      error: "Exactly one property, workloadId, is required.",
    };
  }
  const workloadId = trimmedString(rawInput.workloadId);
  return workloadId
    ? { workloadId }
    : { error: "workloadId must be a non-empty string." };
}

/**
 * Adds one row's citation metadata to the run-scoped index.
 *
 * Uses the shared augment-never-delete policy rather than assigning the row's
 * record wholesale: restore replays closed rows into an index that a resumed
 * direct-research pack may already have enriched, and a row that knows only an
 * FQL formula must not erase the source and document behind the same ID.
 */
function mergeCitationIndex(
  ctx: ExecutorToolRuntimeContext,
  result: ExecuteQueryResult,
): void {
  const index = (ctx.accumulators.citationNoteIndex ??= {});
  for (const citationId of normalizedCitationIds(result)) {
    const record = result.citationNoteIndex[citationId];
    if (!record) continue;
    mergeCitationNoteRecord(index, citationId, {
      citation_id: citationId,
      ...(record.source ? { source: record.source } : {}),
      ...(record.document ? { document: record.document } : {}),
      ...(record.category ? { category: record.category } : {}),
      ...(record.company ? { company: record.company } : {}),
      ...(record.publishedDate
        ? { published_date: record.publishedDate }
        : {}),
      ...(record.ticker ? { ticker: record.ticker } : {}),
      ...(record.fqlFormula ? { fql_formula: record.fqlFormula } : {}),
      ...(record.field ? { field: record.field } : {}),
    });
  }
}

function normalizedCitationIds(result: ExecuteQueryResult): string[] {
  return Array.from(
    new Set(
      result.citationIds
        .map((citationId) => citationId.trim())
        .filter(Boolean),
    ),
  );
}

export function getTabularQueryWorkloadInstruction(): string {
  return `

# Aligned worksheet-column research
When one prompt must populate one aligned output column for a finite worksheet
company list, use ${START_TABULAR_QUERY_WORKLOAD_TOOL_NAME}, then alternate
${NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME} and
${COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME} until every registered row is
closed. Usable results are written and read-back verified; failed rows are left
blank and reported separately. Do not call the ordinary research tool for that
same column. This workload retrieves facts that live in documents or on the web.
It also handles routed FactSet metrics such as price, market cap, revenue, and
other fundamentals figures. Once this workload owns a column, do not call
generate_formula for that column. For a quantitative metric, set responseType to NUMBER and
word the prompt as a bare numeric request naming the metric, period and currency,
such as "Return the latest reported annual revenue as a number in USD". Leaving
responseType as TEXT or asking for the figure "with currency and unit" resolves
the column onto per-document retrieval, which spends one search per row and fails
a large share of them, where the numeric wording reaches the structured client
that answers a whole batch in one request. Supply only ranges and request options; row values and research
results stay inside run state. For an all/every-row user request, the runtime
extends aligned ranges to the last populated source row using live sheet metadata;
an explicit finite range in the user's request remains bounded. When the user asks
about the companies in the sheet without naming a destination, write into the first
empty column to the right of that list and give it a header describing the answer,
rather than answering in chat only or overwriting a populated column. Output rows that
already hold a value or formula are skipped, so a column left incomplete by an
earlier run can be registered again to fill only its still-blank rows; a range
whose cells are all populated is rejected, and the answer to that rejection is a
different output column, never clearing the data that is already there. This
workload writes typed values, not formulas: describe and plan it as tabular data
retrieval, do not add formula refresh work, and do not register a second workload
for the same output after completion because each commit already performs exact
read-back verification. When a wave reports ${TABULAR_SERVICE_UNAVAILABLE_REASON},
the query service is down: commit that wave to keep its usable rows, then stop and
tell the user which rows stayed blank for a later rerun. Rows that fail with
NO_DATA_RETURNED are rows the service holds no value for, so rewording the prompt
and running the same metric again returns the same blanks: report those rows
instead of retrying. A derived ratio is the common cause, and the answer is to
retrieve each component into its own column and compute the ratio from them.
`;
}

export class TabularQueryWorkloadController
  implements ExecutorWorkloadParticipant<TabularQueryWorkloadCompletionIssue>
{
  private workload: TabularWorkload | null = null;
  private workloadSequence = 0;
  private version = 0;
  private verifiedUsableRows = 0;
  private abortListenerAttached = false;
  private readonly dateSystemPromises = new Map<
    string,
    Promise<ExcelDateSystem | null>
  >();
  /**
   * Settled probe outcomes mirrored synchronously so {@link snapshot} can carry
   * them into the checkpoint. Without this a `null` probe taken before a pause
   * would be re-run after resume, writing earlier rows as ISO text and later
   * rows as serials in the same column.
   */
  private readonly dateSystemOutcomes = new Map<
    string,
    ExcelDateSystem | null
  >();
  private readonly client: TabularQueryWorkloadClient;
  private readonly identityResolver: TabularIdentityResolver;
  private readonly limits: {
    columnMaxInFlightBatches: number;
    cellMaxInFlightSingles: number;
  };
  private researchScope: {
    dataSources: string[];
    userFileIds: string[];
  };
  private readonly handleRuntimeAbort = (): void => {
    this.preserveInterruptedWave();
  };

  constructor(private readonly ctx: ExecutorToolRuntimeContext) {
    this.client = ctx.tabularQueryClient ?? DEFAULT_CLIENT;
    this.identityResolver =
      ctx.tabularIdentityResolver ?? resolveFactSetTabularIdentity;
    this.limits = {
      columnMaxInFlightBatches: clampTabularConcurrency(
        ctx.excelTabularQuery?.columnMaxInFlightBatches,
      ),
      cellMaxInFlightSingles: clampTabularConcurrency(
        ctx.excelTabularQuery?.cellMaxInFlightSingles,
      ),
    };
    this.researchScope = trustedResearchScope(ctx.researchContext);
  }

  /**
   * Attach this run's Skill guidance to an outgoing tabular request.
   *
   * Applied per request instead of stored in `queryOptions` because those are
   * checkpointed: a resumed workload picks up whatever policy the live run
   * carries rather than replaying the one frozen at pause.
   */
  private guided<T extends { selectedSkillsGuidance?: string }>(request: T): T {
    const guidance = this.ctx.userSkillGuidance;
    if (!guidance) return request;
    return { ...request, selectedSkillsGuidance: guidance };
  }

  private buildLangfuseMetadata(
    callContext?: RuntimeToolCallContext,
  ): Record<string, unknown> | undefined {
    if (!this.ctx.langfuseContext || !callContext?.callId) return undefined;
    return buildExcelLangfuseMetadata(this.ctx.langfuseContext, {
      phase: "excel_unified_agent",
      parentSpanKey: callContext.callId,
    });
  }

  get hasWorkload(): boolean {
    return this.workload !== null;
  }

  get activeWorkloadId(): string | null {
    return this.workload?.id ?? null;
  }

  get ownerStepId(): string | null {
    return this.workload?.stepId ?? null;
  }

  get progressVersion(): number {
    return this.version;
  }

  get usableResultCount(): number {
    return this.verifiedUsableRows;
  }

  get hasUsableVerifiedResult(): boolean {
    return this.isComplete && this.verifiedUsableRows > 0;
  }

  get failedResultCount(): number {
    return (
      this.workload?.rows.filter(
        (row) => row.status === "closed" && row.failureCode,
      ).length ?? 0
    );
  }

  get isComplete(): boolean {
    return Boolean(
      this.workload &&
        this.workload.activeWaveRows.length === 0 &&
        this.workload.rows.every((row) => row.status === "closed"),
    );
  }

  snapshot(): TabularQueryWorkloadCheckpoint | undefined {
    const workload = this.workload;
    if (!workload) return undefined;
    return cloneJsonSafe({
      version: 1,
      workloadSequence: this.workloadSequence,
      progressVersion: this.version,
      verifiedUsableRows: this.verifiedUsableRows,
      researchScope: {
        dataSources: [...this.researchScope.dataSources],
        userFileIds: [...this.researchScope.userFileIds],
      },
      dateSystemProbes: Array.from(this.dateSystemOutcomes.entries()),
      workload: {
        id: workload.id,
        stepId: workload.stepId,
        sheetName: workload.sheetName,
        companyAddress: workload.companyAddress,
        companyIdAddress: workload.companyIdAddress,
        outputAddress: workload.outputAddress,
        companyRange: { ...workload.companyRange },
        companyIdRange: workload.companyIdRange
          ? { ...workload.companyIdRange }
          : null,
        outputRange: { ...workload.outputRange },
        prompt: workload.prompt,
        responseType: workload.responseType,
        queryOptions: workload.queryOptions,
        mode: workload.mode,
        precomputedFunctionCalls: workload.precomputedFunctionCalls,
        columnBatchSize: workload.columnBatchSize,
        protectedCompanySnapshots: Array.from(
          workload.protectedCompanySnapshots.entries(),
        ),
        protectedCompanyIdSnapshots: workload.protectedCompanyIdSnapshots
          ? Array.from(workload.protectedCompanyIdSnapshots.entries())
          : null,
        rows: workload.rows,
        prefilledOutputRows: [...workload.prefilledOutputRows],
        activeWaveRows: [...workload.activeWaveRows],
        identityCache: Array.from(workload.identityCache.entries()),
        executionCache: Array.from(workload.executionCache.entries()),
        identitiesByLabel: Array.from(
          workload.identitiesByLabel,
          ([key, identities]) => [key, Array.from(identities)],
        ),
        inFlightExecutionKeys: Array.from(workload.inFlightExecutionKeys),
        waveInterrupted: workload.waveInterrupted,
        blocked: workload.blocked,
      },
    } satisfies TabularQueryWorkloadCheckpoint);
  }

  /**
   * Restores a persisted workload only after proving that the workbook still
   * matches every protected source and output invariant.
   */
  async restore(checkpoint: TabularQueryWorkloadCheckpoint): Promise<void> {
    if (this.workload) {
      throw new Error("A tabular query workload is already registered.");
    }
    if (checkpoint.version !== 1 || !checkpoint.workload) {
      throw new Error("The tabular workload checkpoint version is not supported.");
    }
    if (this.ctx.worksheetWorkloadOutputScope) {
      throw new Error(
        "Another worksheet workload already owns a protected output range.",
      );
    }

    const saved = cloneJsonSafe(checkpoint.workload);
    const companyRange = parseA1Range(saved.companyAddress);
    const outputRange = parseA1Range(saved.outputAddress);
    const companyIdRange = saved.companyIdAddress
      ? parseA1Range(saved.companyIdAddress)
      : null;
    if (
      !companyRange ||
      !outputRange ||
      (saved.companyIdAddress && !companyIdRange) ||
      !rangesEqual(companyRange, saved.companyRange) ||
      !rangesEqual(outputRange, saved.outputRange) ||
      Boolean(companyIdRange) !== Boolean(saved.companyIdRange) ||
      (companyIdRange &&
        saved.companyIdRange &&
        !rangesEqual(companyIdRange, saved.companyIdRange))
    ) {
      throw new Error("The tabular workload checkpoint contains invalid ranges.");
    }
    if (
      !saved.id.trim() ||
      !saved.stepId.trim() ||
      !saved.sheetName.trim() ||
      !Array.isArray(saved.protectedCompanySnapshots) ||
      (companyIdRange
        ? !Array.isArray(saved.protectedCompanyIdSnapshots)
        : saved.protectedCompanyIdSnapshots !== null) ||
      !Array.isArray(saved.rows) ||
      !Array.isArray(saved.activeWaveRows)
    ) {
      throw new Error("The tabular workload checkpoint is incomplete.");
    }

    const protectedCompanySnapshots = parseProtectedSnapshotEntries(
      saved.protectedCompanySnapshots,
      companyRange,
      "company range",
    );
    const protectedCompanyIdSnapshots = companyIdRange
      ? parseProtectedSnapshotEntries(
          saved.protectedCompanyIdSnapshots,
          companyIdRange,
          "company identifier range",
        )
      : null;
    const prefilledOutputRows = saved.prefilledOutputRows ?? [];
    if (
      !Array.isArray(prefilledOutputRows) ||
      prefilledOutputRows.some(
        (rowNumber) =>
          !Number.isInteger(rowNumber) ||
          rowNumber < outputRange.startRow ||
          rowNumber > outputRange.endRow,
      )
    ) {
      throw new Error(
        "The tabular workload checkpoint contains invalid pre-filled output rows.",
      );
    }
    const prefilledOutputRowSet = new Set(prefilledOutputRows);
    const rowNumbers = new Set<number>();
    for (const row of saved.rows) {
      const protectedSource = protectedCompanySnapshots.get(row.rowNumber);
      const protectedId = protectedCompanyIdSnapshots?.get(row.rowNumber);
      if (
        !Number.isInteger(row.rowNumber) ||
        row.rowNumber < companyRange.startRow ||
        row.rowNumber > companyRange.endRow ||
        rowNumbers.has(row.rowNumber) ||
        row.sourceCell !== cellAddress(companyRange, row.rowNumber) ||
        row.outputCell !== cellAddress(outputRange, row.rowNumber) ||
        prefilledOutputRowSet.has(row.rowNumber) ||
        (companyIdRange &&
          row.idCell !== cellAddress(companyIdRange, row.rowNumber)) ||
        (!companyIdRange && row.idCell !== undefined) ||
        (row.status !== "pending" &&
          row.status !== "active" &&
          row.status !== "closed") ||
        !protectedSource ||
        !protectedSnapshotsEqual(row.sourceSnapshot, protectedSource) ||
        (companyIdRange &&
          (!row.idSnapshot ||
            !protectedId ||
            !protectedSnapshotsEqual(row.idSnapshot, protectedId)))
      ) {
        throw new Error("The tabular workload checkpoint contains invalid row state.");
      }
      rowNumbers.add(row.rowNumber);
    }
    const activeRows = saved.rows
      .filter((row) => row.status === "active")
      .map((row) => row.rowNumber)
      .sort((left, right) => left - right);
    const savedActiveRows = [...saved.activeWaveRows].sort(
      (left, right) => left - right,
    );
    if (
      activeRows.length !== savedActiveRows.length ||
      activeRows.some((rowNumber, index) => rowNumber !== savedActiveRows[index])
    ) {
      throw new Error(
        "The tabular workload checkpoint has inconsistent active-wave state.",
      );
    }

    const workload: TabularWorkload = {
      ...saved,
      companyRange,
      companyIdRange,
      outputRange,
      protectedCompanySnapshots,
      protectedCompanyIdSnapshots,
      rows: saved.rows,
      prefilledOutputRows,
      columnBatchSize: saved.columnBatchSize ?? TABULAR_COLUMN_BATCH_SIZE,
      identityCache: new Map(saved.identityCache),
      executionCache: new Map(saved.executionCache),
      identitiesByLabel: new Map(
        saved.identitiesByLabel.map(([key, identities]) => [
          key,
          new Set(identities),
        ]),
      ),
      inFlightExecutionKeys: new Set(saved.inFlightExecutionKeys),
      // The outage stop only applies to the run that observed it. A resume is a
      // deliberate later attempt, and the stop re-arms if the service is still
      // down, so rehydrating it would leave the pending rows unrunnable forever.
      blocked: false,
    };
    const activeExecutionKeys = new Set(
      workload.rows
        .filter((row) => row.status === "active" && row.identity)
        .map(executionIdentityKey),
    );
    if (
      Array.from(workload.inFlightExecutionKeys).some(
        (key) => !activeExecutionKeys.has(key),
      )
    ) {
      throw new Error(
        "The tabular workload checkpoint has inconsistent in-flight request state.",
      );
    }

    this.ctx.worksheetWorkloadOutputScope = {
      workloadId: workload.id,
      workloadLabel: "Tabular query workload",
      ownerStepId: workload.stepId,
      sheetName: workload.sheetName,
      targetAddress: workload.outputAddress,
    };
    try {
      throwIfAborted(this.ctx.signal);
      const [companyRead, outputRead, companyIdRead] = await Promise.all([
        readSingleColumnSnapshots(
          this.ctx,
          workload.sheetName,
          workload.companyAddress,
          workload.companyRange,
          { allowCsvValueOnlyEvidence: true },
        ),
        readSingleColumnSnapshots(
          this.ctx,
          workload.sheetName,
          workload.outputAddress,
          workload.outputRange,
        ),
        workload.companyIdRange && workload.companyIdAddress
          ? readSingleColumnSnapshots(
              this.ctx,
              workload.sheetName,
              workload.companyIdAddress,
              workload.companyIdRange,
              { allowCsvValueOnlyEvidence: true },
            )
          : Promise.resolve<SingleColumnSnapshotRead>({}),
      ]);
      throwIfAborted(this.ctx.signal);
      const readError =
        companyRead.error ?? outputRead.error ?? companyIdRead.error;
      if (
        readError ||
        !companyRead.snapshots ||
        !outputRead.snapshots ||
        (workload.companyIdRange && !companyIdRead.snapshots)
      ) {
        throw new Error(
          readError ?? "The protected tabular ranges could not be re-read safely.",
        );
      }
      for (
        let rowNumber = workload.companyRange.startRow;
        rowNumber <= workload.companyRange.endRow;
        rowNumber += 1
      ) {
        const currentSource = companyRead.snapshots.get(rowNumber);
        const protectedSource = workload.protectedCompanySnapshots.get(rowNumber);
        const currentId = companyIdRead.snapshots?.get(rowNumber);
        const protectedId = workload.protectedCompanyIdSnapshots?.get(rowNumber);
        if (
          !currentSource ||
          !protectedSource ||
          !protectedSnapshotsEqual(currentSource, protectedSource) ||
          (workload.companyIdRange &&
            (!currentId ||
              !protectedId ||
              !protectedSnapshotsEqual(currentId, protectedId)))
        ) {
          throw new Error(
            `${workload.sheetName}!row ${rowNumber} source or identifier changed ` +
              "after the workload was suspended.",
          );
        }
      }

      const rowsByNumber = new Map(
        workload.rows.map((row) => [row.rowNumber, row]),
      );
      for (
        let rowNumber = workload.outputRange.startRow;
        rowNumber <= workload.outputRange.endRow;
        rowNumber += 1
      ) {
        const row = rowsByNumber.get(rowNumber);
        const currentOutput = outputRead.snapshots.get(rowNumber);
        if (!currentOutput) {
          throw new Error(
            `${workload.sheetName}!${cellAddress(workload.outputRange, rowNumber)} ` +
              "could not be verified.",
          );
        }
        if (!row) {
          if (
            !prefilledOutputRowSet.has(rowNumber) &&
            !isBlankSnapshot(currentOutput)
          ) {
            throw new Error(
              `${workload.sheetName}!${cellAddress(workload.outputRange, rowNumber)} ` +
                "changed after the workload was suspended.",
            );
          }
          continue;
        }

        const currentSource = companyRead.snapshots.get(rowNumber);
        const currentId = companyIdRead.snapshots?.get(rowNumber);
        if (
          !currentSource ||
          !snapshotsEqual(currentSource, row.sourceSnapshot) ||
          (row.idSnapshot &&
            (!currentId || !snapshotsEqual(currentId, row.idSnapshot)))
        ) {
          throw new Error(
            `${workload.sheetName}!row ${rowNumber} source or identifier changed ` +
              "after the workload was suspended.",
          );
        }

        if (row.status === "closed" && row.verifiedUsable) {
          if (
            row.committedValue === undefined ||
            !outputMatchesExpected(currentOutput, row.committedValue)
          ) {
            throw new Error(
              `${workload.sheetName}!${row.outputCell} no longer matches its ` +
                "exact committed tabular value.",
            );
          }
          continue;
        }
        if (row.status === "active" && row.writeAttempted) {
          const expected = row.preparedWrite?.value;
          if (
            expected === undefined ||
            (!isBlankSnapshot(currentOutput) &&
              !outputMatchesExpected(currentOutput, expected))
          ) {
            throw new Error(
              `${workload.sheetName}!${row.outputCell} has an ambiguous prior write.`,
            );
          }
          continue;
        }
        if (!isBlankSnapshot(currentOutput)) {
          throw new Error(
            `${workload.sheetName}!${row.outputCell} must remain blank and formula-free ` +
              "before the workload can resume.",
          );
        }
      }

      this.workload = workload;
      this.workloadSequence = Math.max(
        checkpoint.workloadSequence,
        Number(saved.id.match(/(\d+)$/)?.[1] ?? 0),
      );
      this.version = checkpoint.progressVersion + 1;
      this.verifiedUsableRows = workload.rows.filter(
        (row) => row.status === "closed" && row.verifiedUsable,
      ).length;
      this.researchScope = {
        dataSources: [...checkpoint.researchScope.dataSources],
        userFileIds: [...checkpoint.researchScope.userFileIds],
      };
      // Rows closed before the pause are never re-queried, so their citation
      // metadata would otherwise be unreachable: a post-resume write citing one
      // of those IDs could not resolve a Note. The row results are the lock
      // that prevents re-querying, and this is what makes them usable evidence.
      for (const row of workload.rows) {
        if (row.status === "closed" && row.result) {
          mergeCitationIndex(this.ctx, row.result);
        }
      }
      // Pin the resumed workload to the representation it already committed.
      // A repin changes the cache key, so the probe legitimately reruns.
      const cacheKey = this.dateSystemCacheKey();
      for (const entry of checkpoint.dateSystemProbes ?? []) {
        if (!Array.isArray(entry) || entry[0] !== cacheKey) continue;
        const dateSystem = entry[1] ?? null;
        this.dateSystemPromises.set(cacheKey, Promise.resolve(dateSystem));
        this.dateSystemOutcomes.set(cacheKey, dateSystem);
      }
      this.attachAbortListener();
      this.releaseOutputScopeIfComplete();
    } catch (error) {
      this.releaseOutputScope(workload.id);
      throw error;
    }
  }

  reset(): void {
    const workload = this.workload;
    if (workload) {
      this.releaseOutputScope(workload.id);
      this.workload = null;
      this.verifiedUsableRows = 0;
      this.version += 1;
    }
    this.detachAbortListener();
  }

  completionIssue(): TabularQueryWorkloadCompletionIssue | null {
    const workload = this.workload;
    if (!workload || this.isComplete) return null;
    const remainingRows = workload.rows.filter(
      (row) => row.status !== "closed",
    );
    if (workload.activeWaveRows.length > 0) {
      if (workload.waveInterrupted) {
        return {
          issue: "tabular_query_workload_pending",
          workloadId: workload.id,
          nextTool: NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
          remainingRows: remainingRows.length,
          nextRowNumbers: [...workload.activeWaveRows],
          instruction:
            `Resume the interrupted tabular wave with ` +
            `${NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME}. Acknowledged outcomes ` +
            "will be reused and ambiguous in-flight requests will not be billed again.",
        };
      }
      return {
        issue: "tabular_query_workload_wave_uncommitted",
        workloadId: workload.id,
        nextTool: COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
        remainingRows: remainingRows.length,
        nextRowNumbers: [...workload.activeWaveRows],
        instruction:
          `Commit and close the active tabular rows with ` +
          `${COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME}; usable writes will be verified.`,
      };
    }
    // A service-outage stop is terminal for this run once its active wave has
    // been committed. Keep later rows pending in the checkpoint, but do not
    // force another wave that this same workload deliberately refuses to run.
    if (workload.blocked) return null;
    return {
      issue: "tabular_query_workload_pending",
      workloadId: workload.id,
      nextTool: NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
      remainingRows: remainingRows.length,
      nextRowNumbers: remainingRows
        .slice(0, tabularRowsPerWave(workload.mode, this.limits))
        .map((row) => row.rowNumber),
      instruction:
        `${remainingRows.length} tabular row(s) remain. Call ` +
        `${NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME} for workload ${workload.id}.`,
    };
  }

  describeIncompleteRows(): string[] {
    const workload = this.workload;
    if (!workload) return [];
    return workload.rows
      .filter((row) => row.status !== "closed")
      .slice(0, 50)
      .map(
        (row) =>
          `${workload.sheetName}!row ${row.rowNumber}: ${row.status}`,
      );
  }

  async start(
    rawInput: unknown,
    _signal?: AbortSignal,
    callContext?: RuntimeToolCallContext,
  ): Promise<string> {
    if (this.ctx.excelTabularQuery?.enabled !== true) {
      return JSON.stringify({
        ok: false,
        error: "The Excel tabular query capability is disabled for this run.",
      });
    }
    throwIfAborted(this.ctx.signal);
    const parsed = parseStartInput(rawInput);
    if (!parsed.value) {
      return JSON.stringify({ ok: false, error: parsed.error });
    }
    const input = parsed.value;
    if (
      this.researchScope.dataSources.length === 0 &&
      this.researchScope.userFileIds.length === 0
    ) {
      return JSON.stringify({
        ok: false,
        error:
          "Tabular research requires at least one trusted data source or user file.",
      });
    }
    if (
      this.researchScope.userFileIds.length >
      TABULAR_MAX_USER_FILE_IDS_PER_TARGET
    ) {
      return JSON.stringify({
        ok: false,
        error:
          `Tabular research accepts at most ${TABULAR_MAX_USER_FILE_IDS_PER_TARGET} ` +
          `selected files per company row, but ${this.researchScope.userFileIds.length} ` +
          "files are selected. Ask the user to narrow the file selection, then " +
          "start the workload again.",
        maxUserFileIds: TABULAR_MAX_USER_FILE_IDS_PER_TARGET,
        selectedUserFileIds: this.researchScope.userFileIds.length,
      });
    }
    const ownerStep = this.ctx.planState.steps.find(
      (step) => step.id === input.stepId,
    );
    if (!ownerStep) {
      return JSON.stringify({
        ok: false,
        error: `Unknown stepId: "${input.stepId}".`,
      });
    }
    if (
      ownerStep.status === "success" ||
      ownerStep.status === "warning" ||
      ownerStep.status === "failed" ||
      ownerStep.status === "recovered" ||
      ownerStep.status === "paused"
    ) {
      return JSON.stringify({
        ok: false,
        error: `stepId "${input.stepId}" is already terminal.`,
      });
    }
    if (this.workload && !this.isComplete) {
      return JSON.stringify({
        ok: false,
        error:
          `Workload ${this.workload.id} is already registered for this run.`,
      });
    }
    if (this.ctx.worksheetWorkloadOutputScope) {
      return JSON.stringify({
        ok: false,
        error:
          "Another worksheet workload already owns a protected output range.",
      });
    }

    let companyAddress = input.companyAddress;
    let outputAddress = input.outputAddress;
    let companyIdAddress = input.companyIdAddress;
    let companyRange = parseA1Range(companyAddress);
    let outputRange = parseA1Range(outputAddress);
    let companyIdRange = companyIdAddress
      ? parseA1Range(companyIdAddress)
      : null;
    if (!companyRange || !outputRange || (input.companyIdAddress && !companyIdRange)) {
      return JSON.stringify({
        ok: false,
        error:
          "All range addresses must be finite unqualified A1 cell ranges.",
      });
    }
    if (
      companyRange.startColumn !== companyRange.endColumn ||
      outputRange.startColumn !== outputRange.endColumn ||
      (companyIdRange &&
        companyIdRange.startColumn !== companyIdRange.endColumn)
    ) {
      return JSON.stringify({
        ok: false,
        error: "Company, optional ID, and output ranges must each be one column.",
      });
    }
    const companyStartRow = companyRange.startRow;
    const companyEndRow = companyRange.endRow;
    const rangesAreAligned = (range: ParsedA1Range): boolean =>
      range.startRow === companyStartRow &&
      range.endRow === companyEndRow;
    if (!rangesAreAligned(outputRange) || (companyIdRange && !rangesAreAligned(companyIdRange))) {
      return JSON.stringify({
        ok: false,
        error: "Company, optional ID, and output ranges must cover the same rows.",
      });
    }
    if (
      rangesOverlap(companyRange, outputRange) ||
      (companyIdRange && rangesOverlap(companyIdRange, outputRange))
    ) {
      return JSON.stringify({
        ok: false,
        error: "The protected output range must not overlap any source range.",
      });
    }
    if (companyIdRange && rangesOverlap(companyRange, companyIdRange)) {
      // Overlapping ranges pass the alignment check but make every label its
      // own identifier, which would be promoted to an authoritative FactSet ID.
      return JSON.stringify({
        ok: false,
        error:
          "The company and company identifier ranges must not overlap; each " +
          "row needs a distinct label cell and identifier cell.",
      });
    }

    if (requestsAllPopulatedTabularRows(this.ctx.session.userRequest)) {
      const resolvedScope = await resolveAllPopulatedTabularEndRow(
        this.ctx,
        input.sheetName,
        companyRange,
      );
      if (resolvedScope.error || resolvedScope.endRow === undefined) {
        return JSON.stringify({
          ok: false,
          error:
            resolvedScope.error ??
            "The complete populated company range could not be resolved.",
        });
      }
      companyRange = {
        ...companyRange,
        endRow: resolvedScope.endRow,
      };
      outputRange = {
        ...outputRange,
        endRow: resolvedScope.endRow,
      };
      companyIdRange = companyIdRange
        ? { ...companyIdRange, endRow: resolvedScope.endRow }
        : null;
      companyAddress = rowRangeAddress(
        companyRange,
        companyRange.startRow,
        companyRange.endRow,
      );
      outputAddress = rowRangeAddress(
        outputRange,
        outputRange.startRow,
        outputRange.endRow,
      );
      companyIdAddress = companyIdRange
        ? rowRangeAddress(
            companyIdRange,
            companyIdRange.startRow,
            companyIdRange.endRow,
          )
        : null;
    }

    const [companyRead, outputRead, companyIdRead] = await Promise.all([
      readSingleColumnSnapshots(
        this.ctx,
        input.sheetName,
        companyAddress,
        companyRange,
        { allowCsvValueOnlyEvidence: true },
      ),
      readSingleColumnSnapshots(
        this.ctx,
        input.sheetName,
        outputAddress,
        outputRange,
      ),
      companyIdRange && companyIdAddress
        ? readSingleColumnSnapshots(
            this.ctx,
            input.sheetName,
            companyIdAddress,
            companyIdRange,
            { allowCsvValueOnlyEvidence: true },
          )
        : Promise.resolve<{
            snapshots?: Map<number, CellSnapshot>;
            error?: string;
          }>({ snapshots: undefined }),
    ]);
    throwIfAborted(this.ctx.signal);
    const readError =
      companyRead.error ?? outputRead.error ?? companyIdRead.error;
    if (
      readError ||
      !companyRead.snapshots ||
      !outputRead.snapshots ||
      (companyIdRange && !companyIdRead.snapshots)
    ) {
      return JSON.stringify({
        ok: false,
        error: readError ?? "The aligned ranges could not be snapshotted.",
      });
    }

    // A rerun over a partly answered column is the normal recovery path after a
    // service outage, so an occupied output cell excludes its row instead of
    // rejecting the range. Nothing this workload owns can overwrite one.
    const prefilledOutputRows: number[] = [];
    for (
      let rowNumber = outputRange.startRow;
      rowNumber <= outputRange.endRow;
      rowNumber += 1
    ) {
      const snapshot = outputRead.snapshots.get(rowNumber);
      if (!snapshot) {
        return JSON.stringify({
          ok: false,
          error: `Output row ${rowNumber} could not be snapshotted.`,
        });
      }
      if (!isBlankSnapshot(snapshot)) prefilledOutputRows.push(rowNumber);
    }
    const prefilledOutputRowSet = new Set(prefilledOutputRows);

    const rows: TabularWorkloadRow[] = [];
    for (
      let rowNumber = companyRange.startRow;
      rowNumber <= companyRange.endRow;
      rowNumber += 1
    ) {
      if (prefilledOutputRowSet.has(rowNumber)) continue;
      const sourceSnapshot = companyRead.snapshots.get(rowNumber);
      const idSnapshot = companyIdRead.snapshots?.get(rowNumber);
      const label =
        sourceSnapshot?.value === null || sourceSnapshot?.value === undefined
          ? ""
          : String(sourceSnapshot.value).trim();
      if (!label) continue;
      if (label.length > 512) {
        return JSON.stringify({
          ok: false,
          error: `Company label at row ${rowNumber} exceeds 512 characters.`,
        });
      }
      const providedCompanyId =
        idSnapshot?.value === null || idSnapshot?.value === undefined
          ? ""
          : String(idSnapshot.value).trim();
      if (providedCompanyId.length > 512) {
        return JSON.stringify({
          ok: false,
          error: `Company identifier at row ${rowNumber} exceeds 512 characters.`,
        });
      }
      rows.push({
        rowNumber,
        sourceCell: cellAddress(companyRange, rowNumber),
        ...(companyIdRange
          ? { idCell: cellAddress(companyIdRange, rowNumber) }
          : {}),
        outputCell: cellAddress(outputRange, rowNumber),
        label,
        labelKey: normalizeLabel(label),
        sourceSnapshot: sourceSnapshot as CellSnapshot,
        ...(idSnapshot ? { idSnapshot } : {}),
        ...(providedCompanyId ? { providedCompanyId } : {}),
        ...(providedCompanyId
          ? { identity: { companyId: providedCompanyId } }
          : {}),
        status: "pending",
      });
    }
    if (rows.length === 0 && prefilledOutputRows.length > 0) {
      // Skipping written cells lets a second workload finish a column an outage
      // left half filled. A range with nothing left to write is not that: it
      // points at a column this run never wrote, so reporting it as finished
      // invites the caller to clear whatever the range already holds.
      return JSON.stringify({
        ok: false,
        error:
          `Every output cell in ${input.sheetName}!${outputAddress} already holds a value, ` +
          "so this range is not an unfinished workload. Target an empty output column instead. " +
          "Do not clear a populated column to make room for the answers.",
        skippedPrefilledRows: prefilledOutputRows.length,
      });
    }
    if (rows.length > TABULAR_METADATA_MAX_TARGETS) {
      return JSON.stringify({
        ok: false,
        error:
          `Tabular metadata currently supports at most ${TABULAR_METADATA_MAX_TARGETS} ` +
          "executable rows in one workload.",
        maxRows: TABULAR_METADATA_MAX_TARGETS,
      });
    }

    this.workloadSequence += 1;
    const workloadId = `tabular_query_workload_${this.workloadSequence}`;
    const routedDataSources = routedTabularDataSources(
      input.prompt,
      this.researchScope.dataSources,
    );
    const provisional: TabularWorkload = {
      id: workloadId,
      stepId: input.stepId,
      sheetName: input.sheetName,
      companyAddress,
      companyIdAddress,
      outputAddress,
      companyRange,
      companyIdRange,
      outputRange,
      prompt: input.prompt,
      responseType: input.responseType,
      queryOptions: {
        prompt: input.prompt,
        responseType: input.responseType,
        dataSources: routedDataSources,
        tagOptions: [...input.tagOptions],
        ...(input.noMatchValue !== undefined
          ? { noMatchValue: input.noMatchValue }
          : {}),
        dataFormat: input.dataFormat,
      },
      mode: "column",
      precomputedFunctionCalls: [],
      columnBatchSize: TABULAR_COLUMN_BATCH_SIZE,
      protectedCompanySnapshots: new Map(companyRead.snapshots),
      protectedCompanyIdSnapshots: companyIdRead.snapshots
        ? new Map(companyIdRead.snapshots)
        : null,
      rows,
      prefilledOutputRows,
      activeWaveRows: [],
      identityCache: new Map(),
      executionCache: new Map(),
      identitiesByLabel: new Map(),
      inFlightExecutionKeys: new Set(),
      waveInterrupted: false,
      blocked: false,
    };
    // verifiedUsableRows describes the currently registered workload. The
    // runner persists the completed workload's research contribution
    // separately before a new workload replaces it.
    this.verifiedUsableRows = 0;
    this.workload = provisional;
    this.ctx.worksheetWorkloadOutputScope = {
      workloadId,
      workloadLabel: "Tabular query workload",
      ownerStepId: input.stepId,
      sheetName: input.sheetName,
      targetAddress: outputAddress,
    };
    this.attachAbortListener();

    if (rows.length === 0) {
      this.version += 1;
      this.releaseOutputScopeIfComplete();
      return JSON.stringify({
        ...this.buildSummary(),
        nextAction: "No nonblank company rows require execution.",
      });
    }

    const langfuseMetadata = this.buildLangfuseMetadata(callContext);
    try {
      let dataFormat = input.dataFormat;
      if (
        dataFormat.formatType === "auto" &&
        (input.responseType === "TEXT" ||
          input.responseType === "NUMBER" ||
          input.responseType === "DATE")
      ) {
        const resolved = await this.client.resolveDataFormat(
          this.guided<ResolveDataFormatRequest>({
            prompt: input.prompt,
            responseType: input.responseType,
            dataSources: [...routedDataSources],
          }),
          this.ctx.signal,
          ...optionalLangfuseMetadata(langfuseMetadata),
        );
        if (resolved.dataFormat.formatType === "auto") {
          throw new Error("Resolved data format remained auto.");
        }
        dataFormat = resolved.dataFormat;
      }
      provisional.queryOptions.dataFormat = dataFormat;

      const targets = rows.map((row) =>
        targetForRow(row, this.researchScope.userFileIds),
      );
      let metadata = await this.client.getFunctionCallMetadata(
        this.guided({ ...provisional.queryOptions, targets }),
        this.ctx.signal,
        ...optionalLangfuseMetadata(langfuseMetadata),
      );
      if (metadata.precomputedFunctionCalls.length === 0) {
        throw new Error("Tabular query metadata returned no function calls.");
      }
      if (resolvesOnlyToCrossCompanySearch(metadata.precomputedFunctionCalls)) {
        const fallbackDataSources = perCompanyRetrievalFallback(
          provisional.queryOptions.dataSources,
        );
        const retried = fallbackDataSources
          ? await this.client.getFunctionCallMetadata(
              this.guided({
                ...provisional.queryOptions,
                dataSources: fallbackDataSources,
                targets,
              }),
              this.ctx.signal,
              ...optionalLangfuseMetadata(langfuseMetadata),
            )
          : null;
        if (
          !fallbackDataSources ||
          !retried ||
          retried.precomputedFunctionCalls.length === 0 ||
          resolvesOnlyToCrossCompanySearch(retried.precomputedFunctionCalls)
        ) {
          this.reset();
          return JSON.stringify({
            ok: false,
            error:
              "This column resolved to the cross-company filings search, which answers " +
              "questions that name no company and leaves every row without evidence. " +
              "Ask the user to select a data source that carries this question for a " +
              "single company, such as Web Search, before retrying.",
            resolvedFunctions: metadata.precomputedFunctionCalls.map(
              (call) => call.functionName,
            ),
          });
        }
        provisional.queryOptions.dataSources = fallbackDataSources;
        metadata = retried;
      }
      if (metadata.mode === "cell") {
        const structuredDataSources = structuredFactSetRetryDataSources(
          provisional.queryOptions.dataSources,
          metadata.precomputedFunctionCalls,
        );
        if (structuredDataSources) {
          const structured = await this.client.getFunctionCallMetadata(
            this.guided({
              ...provisional.queryOptions,
              dataSources: structuredDataSources,
              targets,
            }),
            this.ctx.signal,
            ...optionalLangfuseMetadata(langfuseMetadata),
          );
          // Only take the narrowed plan when it actually buys batched column
          // execution; anything else keeps the broader retrieval it replaced.
          if (
            structured.mode === "column" &&
            structured.precomputedFunctionCalls.length > 0
          ) {
            provisional.queryOptions.dataSources = structuredDataSources;
            metadata = structured;
          }
        }
      }
      const mode = metadata.mode;
      provisional.mode = mode;
      provisional.precomputedFunctionCalls = metadata.precomputedFunctionCalls;
      if (mode === "column") {
        const learnedBatchSize = learnedColumnBatchSizes.get(
          columnBatchSizeKey(metadata.precomputedFunctionCalls),
        );
        if (learnedBatchSize !== undefined) {
          provisional.columnBatchSize = Math.min(
            provisional.columnBatchSize,
            learnedBatchSize,
          );
        }
      }
      diag("tool", "tabular_metadata_routing", {
        workloadId,
        mode,
        dataSources: provisional.queryOptions.dataSources,
        functionNames: metadata.precomputedFunctionCalls.map(
          (call) => call.functionName,
        ),
      });

      if (
        mode === "cell" &&
        rows.length > TABULAR_CELL_MAX_EXECUTABLE_ROWS
      ) {
        provisional.blocked = true;
        for (const row of rows) {
          row.failureCode = "CELL_ROW_LIMIT_EXCEEDED";
          row.status = "closed";
        }
        this.version += 1;
        this.releaseOutputScopeIfComplete();
        return JSON.stringify({
          ...this.buildSummary(),
          blocked: true,
          reason: "CELL_ROW_LIMIT_EXCEEDED",
          maxExecutableRows: TABULAR_CELL_MAX_EXECUTABLE_ROWS,
          nextAction:
            "The cell-mode workload was closed before identity or query execution.",
        });
      }

      const requiredIterations = requiredExecutorIterationsForTabular(
        rows.length,
        mode,
        this.limits,
      );
      const remainingIterations = this.ctx.getRemainingExecutorIterations?.();
      if (
        requiredIterations > MAX_EXECUTOR_ITERATIONS ||
        (typeof remainingIterations === "number" &&
          Number.isFinite(remainingIterations) &&
          remainingIterations < requiredIterations)
      ) {
        const available =
          typeof remainingIterations === "number" &&
          Number.isFinite(remainingIterations)
            ? remainingIterations
            : MAX_EXECUTOR_ITERATIONS;
        this.reset();
        return JSON.stringify({
          ok: false,
          error:
            `This workload needs ${requiredIterations} executor iteration(s) from ` +
            `start through terminal headroom, but only ${available} are available.`,
          requiredIterations,
          remainingIterations: available,
        });
      }

      this.version += 1;
      return JSON.stringify({
        ...this.buildSummary(),
        requiredIterations,
        nextAction: `Call ${NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME}.`,
      });
    } catch (error) {
      if (isAbortFailure(error, this.ctx.signal)) {
        this.reset();
        throw tabularAbortError();
      }
      this.reset();
      return JSON.stringify({
        ok: false,
        error: safeErrorMessage(error),
      });
    }
  }

  async executeNextWave(
    rawInput: unknown,
    signal?: AbortSignal,
    callContext?: RuntimeToolCallContext,
  ): Promise<ToolCallDetailedResult> {
    const workload = this.workload;
    const parsedInput = parseWorkloadInput(rawInput);
    const workloadError =
      parsedInput.error ??
      this.validateWorkloadId(parsedInput.workloadId ?? "");
    if (workloadError || !workload) {
      return { output: JSON.stringify({ ok: false, error: workloadError }) };
    }
    const resumingInterruptedWave =
      workload.activeWaveRows.length > 0 && workload.waveInterrupted;
    if (workload.activeWaveRows.length > 0 && !resumingInterruptedWave) {
      return {
        output: JSON.stringify({
          ok: false,
          error:
            `Rows ${workload.activeWaveRows.join(", ")} are still active. ` +
            `Call ${COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME} first.`,
        }),
      };
    }
    const waveRows = resumingInterruptedWave
      ? workload.activeWaveRows
          .map((rowNumber) =>
            workload.rows.find((row) => row.rowNumber === rowNumber),
          )
          .filter((row): row is TabularWorkloadRow => Boolean(row))
      : workload.rows
          .filter((row) => row.status === "pending")
          .slice(0, tabularRowsPerWave(workload.mode, this.limits));
    if (waveRows.length === 0) {
      return { output: JSON.stringify(this.buildSummary()) };
    }
    if (workload.blocked && !resumingInterruptedWave) {
      return {
        output: JSON.stringify({
          ...this.buildSummary(),
          reason: TABULAR_SERVICE_UNAVAILABLE_REASON,
          nextAction: serviceUnavailableNextAction(waveRows.length),
        }),
      };
    }

    const effectiveSignal = signal ?? this.ctx.signal;
    throwIfAborted(effectiveSignal);
    if (resumingInterruptedWave) {
      this.prepareInterruptedWaveForResume(workload, waveRows);
    } else {
      workload.activeWaveRows = waveRows.map((row) => row.rowNumber);
      waveRows.forEach((row) => {
        row.status = "active";
      });
    }
    workload.waveInterrupted = false;
    this.version += 1;

    try {
      await this.resolveWaveIdentities(workload, waveRows, effectiveSignal);
      throwIfAborted(effectiveSignal);
      for (let attempt = 0; ; attempt += 1) {
        if (workload.mode === "column") {
          await this.executeColumnWave(
            workload,
            waveRows,
            effectiveSignal,
            callContext,
          );
        } else {
          await this.executeCellWave(
            workload,
            waveRows,
            effectiveSignal,
            callContext,
          );
        }
        throwIfAborted(effectiveSignal);
        if (!waveExhaustedByServiceFailures(waveRows)) {
          if (
            workload.mode === "column" &&
            workload.columnBatchSize < TABULAR_COLUMN_BATCH_SIZE &&
            waveRows.some((row) => row.result && !row.failureCode)
          ) {
            learnedColumnBatchSizes.set(
              columnBatchSizeKey(workload.precomputedFunctionCalls),
              workload.columnBatchSize,
            );
          }
          break;
        }
        const retryDelayMs = TABULAR_SERVICE_OUTAGE_RETRY_DELAYS_MS[attempt];
        if (retryDelayMs === undefined || !waveOutageLooksTransient(waveRows)) {
          workload.blocked = true;
          break;
        }
        // A batch the upstream edge cut off at its read timeout fails the same
        // way however long the wait, because the work never fits the window.
        // Asking for fewer companies per request is what makes the retry
        // different, and the narrower size holds for the rest of the workload.
        if (workload.mode === "column" && workload.columnBatchSize > 1) {
          workload.columnBatchSize = Math.max(
            1,
            Math.floor(workload.columnBatchSize / 2),
          );
        }
        // A wave that answered some rows proves the service is up, so its
        // failures are about how much one request asked for rather than an
        // outage to sit out. Only a wave that answered nothing waits.
        const answeredSomeRows = waveRows.some(
          (row) => row.result && !row.failureCode,
        );
        const retriedRows = this.discardWaveServiceFailures(workload, waveRows);
        diag("tool", "tabular_service_outage_retry", {
          workloadId: workload.id,
          attempt: attempt + 1,
          retriedRows,
          delayMs: answeredSomeRows ? 0 : retryDelayMs,
          columnBatchSize: workload.columnBatchSize,
        });
        if (!answeredSomeRows) {
          await waitForTabularDelay(retryDelayMs, effectiveSignal);
        }
      }
    } catch (error) {
      this.preserveInterruptedWave();
      if (isAbortFailure(error, effectiveSignal)) throw tabularAbortError();
      throw new TabularQueryWorkloadSuspensionError(
        error instanceof Error
          ? error.message
          : "The tabular query workload was suspended.",
        { cause: error },
      );
    }

    const failedRowDetails = waveRows
      .filter((row) => row.failureCode)
      .map((row) => ({
        rowNumber: row.rowNumber,
        code: row.failureCode,
      }));
    const usableRows = waveRows.filter(
      (row) => row.result && !row.failureCode,
    ).length;
    const pendingRowsAfterWave = workload.rows.filter(
      (row) => row.status === "pending",
    ).length;
    if (waveExhaustedByServiceFailures(waveRows)) {
      workload.blocked = true;
      this.version += 1;
    }
    return {
      output: JSON.stringify({
        ok: true,
        workloadId: workload.id,
        mode: workload.mode,
        waveSize: waveRows.length,
        usableRows,
        failedRowDetails,
        remainingRows: workload.rows.filter(
          (row) => row.status !== "closed",
        ).length,
        ...(workload.blocked
          ? { blocked: true, reason: TABULAR_SERVICE_UNAVAILABLE_REASON }
          : {}),
        nextAction: workload.blocked
          ? `Call ${COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME} to keep the rows ` +
            `that succeeded. ${serviceUnavailableNextAction(pendingRowsAfterWave)}`
          : `Call ${COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME}.`,
      }),
      observationMetadata: {
        workloadId: workload.id,
        mode: workload.mode,
        rowCount: waveRows.length,
        usableRowCount: usableRows,
        failedRowCount: failedRowDetails.length,
      },
    };
  }

  async commitWave(
    rawInput: unknown,
    signal?: AbortSignal,
    callContext?: RuntimeToolCallContext,
  ): Promise<string> {
    const workload = this.workload;
    const parsedInput = parseWorkloadInput(rawInput);
    const workloadError =
      parsedInput.error ??
      this.validateWorkloadId(parsedInput.workloadId ?? "");
    if (workloadError || !workload) {
      return JSON.stringify({ ok: false, error: workloadError });
    }
    if (workload.activeWaveRows.length === 0) {
      return JSON.stringify({
        ok: false,
        error:
          `No active wave exists. Call ` +
          `${NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME} first.`,
      });
    }
    if (workload.waveInterrupted) {
      return JSON.stringify({
        ok: false,
        error:
          "The active tabular wave was interrupted and must be resumed before it can be committed.",
        nextAction: `Call ${NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME}.`,
      });
    }
    const effectiveSignal = signal ?? this.ctx.signal;
    const ensureNotAborted = (): void => {
      if (!effectiveSignal?.aborted) return;
      this.preserveInterruptedWave();
      throwIfAborted(effectiveSignal);
    };
    ensureNotAborted();

    const activeRows = workload.activeWaveRows
      .map((rowNumber) =>
        workload.rows.find((row) => row.rowNumber === rowNumber),
      )
      .filter((row): row is TabularWorkloadRow => Boolean(row));
    const firstRow = Math.min(...activeRows.map((row) => row.rowNumber));
    const lastRow = Math.max(...activeRows.map((row) => row.rowNumber));
    const [sourceRead, outputRead, idRead] = await Promise.all([
      readSingleColumnSnapshots(
        this.ctx,
        workload.sheetName,
        rowRangeAddress(workload.companyRange, firstRow, lastRow),
        {
          ...workload.companyRange,
          startRow: firstRow,
          endRow: lastRow,
        },
        { allowCsvValueOnlyEvidence: true },
      ),
      readSingleColumnSnapshots(
        this.ctx,
        workload.sheetName,
        rowRangeAddress(workload.outputRange, firstRow, lastRow),
        {
          ...workload.outputRange,
          startRow: firstRow,
          endRow: lastRow,
        },
      ),
      workload.companyIdRange
        ? readSingleColumnSnapshots(
            this.ctx,
            workload.sheetName,
            rowRangeAddress(workload.companyIdRange, firstRow, lastRow),
            {
              ...workload.companyIdRange,
              startRow: firstRow,
              endRow: lastRow,
            },
            { allowCsvValueOnlyEvidence: true },
          )
        : Promise.resolve<{
            snapshots?: Map<number, CellSnapshot>;
            error?: string;
          }>({ snapshots: undefined }),
    ]);
    ensureNotAborted();
    const readError = sourceRead.error ?? outputRead.error ?? idRead.error;
    if (
      readError ||
      !sourceRead.snapshots ||
      !outputRead.snapshots ||
      (workload.companyIdRange && !idRead.snapshots)
    ) {
      return JSON.stringify({
        ok: false,
        error: readError ?? "The active wave could not be re-read safely.",
      });
    }

    const dateSystem =
      workload.responseType === "DATE"
        ? await this.resolveDateSystem(workload)
        : null;
    const workloadDataFormat = workload.queryOptions.dataFormat;
    if (!workloadDataFormat) {
      return JSON.stringify({
        ok: false,
        error: "The tabular workload is missing its resolved data format.",
      });
    }
    ensureNotAborted();
    const preparedByRow = new Map<number, PreparedTabularWriteValue>();
    const rowsToWrite: TabularWorkloadRow[] = [];
    const alreadyWrittenRows: TabularWorkloadRow[] = [];
    const ambiguousWriteRows: number[] = [];
    const ambiguousWriteDetails: TabularWriteMismatchDetail[] = [];
    for (const row of activeRows) {
      const currentSource = sourceRead.snapshots.get(row.rowNumber);
      const currentOutput = outputRead.snapshots.get(row.rowNumber);
      const currentId = idRead.snapshots?.get(row.rowNumber);
      const stale =
        !currentSource ||
        !currentOutput ||
        !snapshotsEqual(currentSource, row.sourceSnapshot) ||
        (row.idSnapshot &&
          (!currentId || !snapshotsEqual(currentId, row.idSnapshot)));
      if (stale) {
        row.failureCode = "STALE_WORKBOOK_ROW";
        delete row.result;
        delete row.preparedWrite;
        continue;
      }
      if (row.failureCode || !row.result) {
        if (!isBlankSnapshot(currentOutput)) {
          row.failureCode = "STALE_WORKBOOK_ROW";
        }
        continue;
      }
      const prepared = prepareTypedWriteValue(
        workload.responseType,
        row.result,
        workloadDataFormat,
        dateSystem,
      );
      if (!prepared) {
        row.failureCode = "INVALID_TYPED_RESULT";
        delete row.result;
        delete row.preparedWrite;
        continue;
      }
      const exactPrepared = row.writeAttempted && row.preparedWrite
        ? row.preparedWrite
        : prepared;
      row.preparedWrite = exactPrepared;
      preparedByRow.set(row.rowNumber, exactPrepared);
      if (isBlankSnapshot(currentOutput)) {
        rowsToWrite.push(row);
      } else if (
        row.writeAttempted &&
        outputMatchesExpected(currentOutput, exactPrepared.value)
      ) {
        alreadyWrittenRows.push(row);
      } else if (row.writeAttempted) {
        ambiguousWriteRows.push(row.rowNumber);
        ambiguousWriteDetails.push(
          tabularWriteMismatchDetail(
            row.rowNumber,
            currentOutput,
            exactPrepared.value,
          ),
        );
        diag("write", "tabular_retry_ambiguous_value", {
          sheetName: workload.sheetName,
          cell: row.outputCell,
          expectedType: typeof exactPrepared.value,
          observedType: tabularValueType(currentOutput.value),
          formulaPresent: currentOutput.formula !== null,
          expectedPreview: diagnosticValuePreview(exactPrepared.value),
          observedPreview: diagnosticValuePreview(currentOutput.value),
          formulaPreview: currentOutput.formula
            ? diagnosticValuePreview(currentOutput.formula)
            : null,
        });
      } else {
        row.failureCode = "STALE_WORKBOOK_ROW";
        delete row.result;
        delete row.preparedWrite;
        preparedByRow.delete(row.rowNumber);
      }
    }
    if (ambiguousWriteRows.length > 0) {
      return JSON.stringify({
        ok: false,
        applied: true,
        error:
          "A previously attempted tabular write does not match its exact typed result.",
        ambiguousWriteRows,
        ambiguousWriteDetails,
        retryWith: COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
      });
    }

    const writeGroups = groupConsecutiveRows(
      rowsToWrite,
      (row) => preparedByRow.get(row.rowNumber)?.numberFormat ?? "",
    );
    if (writeGroups.length > 0) {
      ensureNotAborted();
      rowsToWrite.forEach((row) => {
        row.writeAttempted = true;
        if (row.result) mergeCitationIndex(this.ctx, row.result);
      });
      const includeCitationNotes =
        workload.responseType !== "SINGLE_SELECT" &&
        workload.responseType !== "MULTI_SELECT";
      const applyOutput = await executeModifyWorkbookTool(this.ctx, {
        stepId: workload.stepId,
        targetSheet: workload.sheetName,
        modifications: {
          worksheets: [
            {
              sheetName: workload.sheetName,
              ranges: writeGroups.map((group) => ({
                address: rowRangeAddress(
                  workload.outputRange,
                  group[0].rowNumber,
                  group[group.length - 1].rowNumber,
                ),
                action: "fill" as const,
                values: group.map((row) => [
                  preparedByRow.get(row.rowNumber)!.value,
                ]),
                ...(includeCitationNotes
                  ? {
                      citationIdsMatrix: group.map((row) => {
                        const ids = row.result
                          ? normalizedCitationIds(row.result)
                          : [];
                        return [ids.length > 0 ? ids : null];
                      }),
                    }
                  : {}),
                ...(preparedByRow.get(group[0].rowNumber)?.numberFormat
                  ? {
                      format: {
                        numberFormat: {
                          format: preparedByRow.get(group[0].rowNumber)!
                            .numberFormat!,
                        },
                      },
                    }
                  : {}),
              })),
            },
          ],
          message:
            `Committed tabular query rows ${firstRow}-${lastRow}`,
        },
      });
      ensureNotAborted();
      const applyResult = parseToolOutput(applyOutput);
      if (applyResult?.ok !== true || applyResult.applied !== true) {
        return JSON.stringify({
          ok: false,
          applied: applyResult?.applied === true,
          error:
            "The tabular wave write was not fully acknowledged; read-back is required before another commit.",
        });
      }
    }

    const verificationRows = [...rowsToWrite, ...alreadyWrittenRows].filter(
      (row) => !row.failureCode && preparedByRow.has(row.rowNumber),
    );
    const verificationErrors: string[] = [];
    const verificationMismatchDetails: TabularWriteMismatchDetail[] = [];
    for (const group of groupConsecutiveRows(verificationRows)) {
      ensureNotAborted();
      const verification = await verifyTabularWriteGroup(
        this.ctx,
        workload.sheetName,
        workload.outputRange,
        group,
        preparedByRow,
        effectiveSignal,
      );
      ensureNotAborted();
      if (!verification.complete) {
        verificationErrors.push(
          verification.error ??
            `${workload.sheetName}!${group[0].outputCell}:${group.at(-1)?.outputCell} did not verify.`,
        );
        verificationMismatchDetails.push(
          ...(verification.mismatchDetails ?? []),
        );
      }
    }
    if (verificationErrors.length > 0) {
      return JSON.stringify({
        ok: false,
        applied: writeGroups.length > 0,
        error: "Tabular wave read-back verification failed.",
        verificationErrors,
        ...(verificationMismatchDetails.length > 0
          ? { verificationMismatchDetails }
          : {}),
        retryWith: COMMIT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME,
      });
    }

    const failedRowDetails: Array<{ rowNumber: number; code: RowFailureCode }> = [];
    const closedRowNumbers: number[] = [];
    for (const row of activeRows) {
      row.status = "closed";
      closedRowNumbers.push(row.rowNumber);
      if (row.failureCode) {
        failedRowDetails.push({
          rowNumber: row.rowNumber,
          code: row.failureCode,
        });
      } else if (row.result) {
        row.verifiedUsable = true;
        row.committedValue = preparedByRow.get(row.rowNumber)?.value;
        this.verifiedUsableRows += 1;
        mergeCitationIndex(this.ctx, row.result);
      }
    }
    workload.activeWaveRows = [];
    workload.inFlightExecutionKeys.clear();
    workload.waveInterrupted = false;
    this.version += 1;
    callContext?.reportWorkloadProgress?.({
      kind: "closed_rows",
      completed: workload.rows.filter((row) => row.status === "closed")
        .length,
      total: workload.rows.length,
      usable: this.verifiedUsableRows,
      failed: this.failedResultCount,
    });
    this.releaseOutputScopeIfComplete();

    return JSON.stringify({
      ...this.buildSummary(),
      closedRowNumbers,
      failedRowDetails,
      nextAction: this.isComplete
        ? "All registered tabular rows are closed; usable rows were written and verified."
        : workload.blocked
          ? serviceUnavailableNextAction(
              workload.rows.filter((row) => row.status === "pending").length,
            )
          : `Call ${NEXT_TABULAR_QUERY_WORKLOAD_WAVE_TOOL_NAME}.`,
    });
  }

  private async resolveWaveIdentities(
    workload: TabularWorkload,
    waveRows: readonly TabularWorkloadRow[],
    signal?: AbortSignal,
  ): Promise<void> {
    const firstRowByLabel = new Map<string, TabularWorkloadRow>();
    for (const row of waveRows) {
      if (row.identity || row.providedCompanyId) continue;
      if (!firstRowByLabel.has(row.labelKey)) {
        firstRowByLabel.set(row.labelKey, row);
      }
    }
    const freshRows = Array.from(firstRowByLabel.values()).filter(
      (row) => !workload.identityCache.has(row.labelKey),
    );
    const resolutions = await mapWithConcurrencyLimit(
      freshRows,
      TABULAR_IDENTITY_LOOKUP_CONCURRENCY,
      async (row) => {
        throwIfAborted(signal);
        try {
          const result = await this.identityResolver(row.label, signal);
          return { key: row.labelKey, result };
        } catch (error) {
          if (isAbortFailure(error, signal)) throw error;
          if (isProgrammingError(error)) throw error;
          // A dead sign-in fails every remaining label in milliseconds. Left as
          // a per-row resolution it would close the rest of the sheet as
          // unmatched companies, so it suspends the wave with its rows intact.
          if (error instanceof TabularIdentityAuthError) throw error;
          return {
            key: row.labelKey,
            result: { status: "failed" } as FactSetTabularIdentityResolution,
          };
        }
      },
    );
    resolutions.forEach(({ key, result }) => {
      workload.identityCache.set(key, result);
    });

    for (const row of waveRows) {
      if (row.providedCompanyId) {
        row.identity = { companyId: row.providedCompanyId };
      } else {
        const resolution = workload.identityCache.get(row.labelKey);
        if (!resolution || resolution.status !== "resolved") {
          // A definite no-match only rules out the public-company universe.
          // Selected user files can still hold the answer for a private
          // company, so execute the row as a name-only target. A resolver
          // outage stays COMPANY_LOOKUP_FAILED because the label may well be
          // resolvable on retry.
          if (
            resolution?.status === "no_match" &&
            this.researchScope.userFileIds.length > 0
          ) {
            row.identity = { nameOnly: true };
          } else {
            row.failureCode =
              resolution?.status === "no_match"
                ? "COMPANY_NOT_FOUND"
                : "COMPANY_LOOKUP_FAILED";
            continue;
          }
        } else if (!resolution.companyId && !resolution.companyTicker) {
          row.failureCode = "COMPANY_LOOKUP_FAILED";
          continue;
        } else {
          row.identity = {
            ...(resolution.companyId
              ? { companyId: resolution.companyId }
              : {}),
            ...(resolution.companyTicker
              ? { companyTicker: resolution.companyTicker }
              : {}),
          };
        }
      }
      const identityKey = executionIdentityKey(row);
      const identities =
        workload.identitiesByLabel.get(row.labelKey) ?? new Set<string>();
      identities.add(identityKey);
      workload.identitiesByLabel.set(row.labelKey, identities);
    }
  }

  private async executeColumnWave(
    workload: TabularWorkload,
    waveRows: readonly TabularWorkloadRow[],
    signal?: AbortSignal,
    callContext?: RuntimeToolCallContext,
  ): Promise<void> {
    const langfuseMetadata = this.buildLangfuseMetadata(callContext);
    const executableRows = waveRows.filter(
      (row) => !row.failureCode && row.identity,
    );
    const groupsByKey = new Map<string, ExecutionGroup>();
    for (const row of executableRows) {
      const key = executionIdentityKey(row);
      const existing = groupsByKey.get(key);
      if (existing) {
        existing.rows.push(row);
      } else {
        groupsByKey.set(key, { key, representative: row, rows: [row] });
      }
    }

    const freshGroups: ExecutionGroup[] = [];
    for (const group of Array.from(groupsByKey.values())) {
      const cached = workload.executionCache.get(group.key);
      if (cached) {
        this.applyOutcome(group.rows, cached);
      } else {
        freshGroups.push(group);
      }
    }
    const unsafeGroups = freshGroups.filter(
      (group) =>
        (workload.identitiesByLabel.get(
          group.representative.labelKey,
        )?.size ?? 0) > 1,
    );
    const safeGroups = freshGroups.filter(
      (group) => !unsafeGroups.includes(group),
    );
    const tasks: ColumnExecutionTask[] = [
      ...partition(safeGroups, workload.columnBatchSize).map(
        (groups): ColumnExecutionTask => ({ kind: "batch", groups }),
      ),
      ...unsafeGroups.map(
        (group): ColumnExecutionTask => ({ kind: "single", group }),
      ),
    ];
    let completedRequests = 0;
    callContext?.reportWorkloadProgress?.({
      kind: "requests",
      completed: completedRequests,
      total: tasks.length,
    });
    // Execution is billable and never retried after an ambiguous failure. Safe
    // rejections let siblings settle so a local abort cannot discard results.
    const { controller, cleanup } = createLinkedAbortController(signal);
    const waveSignal = controller.signal;
    try {
      await mapWithConcurrencyLimit(
        tasks,
        this.limits.columnMaxInFlightBatches,
        async (task) => {
          throwIfAborted(waveSignal);
          try {
            if (task.kind === "single") {
              workload.inFlightExecutionKeys.add(task.group.key);
              let outcome: StoredExecutionOutcome;
              try {
                const result = await this.client.executeSingleQuery(
                  this.guided({
                    ...workload.queryOptions,
                    target: targetForRow(
                      task.group.representative,
                      this.researchScope.userFileIds,
                    ),
                    precomputedFunctionCalls:
                      workload.precomputedFunctionCalls,
                  }),
                  waveSignal,
                  ...optionalLangfuseMetadata(langfuseMetadata),
                );
                outcome = resultOutcome(workload.responseType, result);
              } catch (error) {
                if (isSafelyRejectedExecutionFailure(error)) {
                  workload.inFlightExecutionKeys.delete(task.group.key);
                  throw error;
                }
                if (isAbortFailure(error, waveSignal)) throw error;
                if (isFatalExecutionFailure(error)) {
                  throw error;
                }
                outcome = {
                  failureCode: "QUERY_EXECUTION_FAILED",
                  transient: isTransientServiceFailure(error),
                };
              }
              this.acknowledgeExecutionOutcome(
                workload,
                task.group.key,
                task.group.rows,
                outcome,
              );
              return [{ group: task.group, outcome }];
            }

            task.groups.forEach((group) => {
              workload.inFlightExecutionKeys.add(group.key);
            });
            try {
              const response = await this.client.executeBatchQuery(
                this.guided({
                  ...workload.queryOptions,
                  targets: task.groups.map((group) =>
                    targetForRow(
                      group.representative,
                      this.researchScope.userFileIds,
                    ),
                  ),
                  precomputedFunctionCalls:
                    workload.precomputedFunctionCalls,
                }),
                waveSignal,
                ...optionalLangfuseMetadata(langfuseMetadata),
              );
              const resultByLabel = new Map<string, ExecuteQueryResult>();
              for (const result of response.results) {
                const key = normalizeLabel(result.companyName);
                if (!resultByLabel.has(key)) resultByLabel.set(key, result);
              }
              const acknowledged = task.groups.map((group) => {
                const result = resultByLabel.get(
                  group.representative.labelKey,
                );
                return {
                  group,
                  outcome: result
                    ? resultOutcome(workload.responseType, result)
                    : {
                        failureCode:
                          "QUERY_RESULT_MISSING" as RowFailureCode,
                      },
                };
              });
              acknowledged.forEach(({ group, outcome }) => {
                this.acknowledgeExecutionOutcome(
                  workload,
                  group.key,
                  group.rows,
                  outcome,
                );
              });
              return acknowledged;
            } catch (error) {
              if (isSafelyRejectedExecutionFailure(error)) {
                task.groups.forEach((group) => {
                  workload.inFlightExecutionKeys.delete(group.key);
                });
                throw error;
              }
              if (isAbortFailure(error, waveSignal)) throw error;
              if (isFatalExecutionFailure(error)) {
                throw error;
              }
              const acknowledged = task.groups.map((group) => ({
                group,
                outcome: {
                  failureCode: "QUERY_EXECUTION_FAILED" as RowFailureCode,
                  transient: isTransientServiceFailure(error),
                },
              }));
              acknowledged.forEach(({ group, outcome }) => {
                this.acknowledgeExecutionOutcome(
                  workload,
                  group.key,
                  group.rows,
                  outcome,
                );
              });
              return acknowledged;
            }
          } finally {
            completedRequests += 1;
            callContext?.reportWorkloadProgress?.({
              kind: "requests",
              completed: completedRequests,
              total: tasks.length,
            });
          }
        },
        {
          onFirstFailure: (error) =>
            abortInFlightOnUnsafeFailure(controller, error),
        },
      );
    } finally {
      cleanup();
    }
  }

  private async executeCellWave(
    workload: TabularWorkload,
    waveRows: readonly TabularWorkloadRow[],
    signal?: AbortSignal,
    callContext?: RuntimeToolCallContext,
  ): Promise<void> {
    const langfuseMetadata = this.buildLangfuseMetadata(callContext);
    const eligibleRows = waveRows.filter(
      (row) => !row.failureCode && row.identity,
    );
    const groupsByKey = new Map<string, ExecutionGroup>();
    for (const row of eligibleRows) {
      const key = executionIdentityKey(row);
      const existing = groupsByKey.get(key);
      if (existing) {
        existing.rows.push(row);
      } else {
        groupsByKey.set(key, {
          key,
          representative: row,
          rows: [row],
        });
      }
    }
    const executableGroups: ExecutionGroup[] = [];
    for (const group of Array.from(groupsByKey.values())) {
      const { key, rows } = group;
      const cached = workload.executionCache.get(key);
      if (cached) {
        this.applyOutcome(rows, cached);
      } else if (rows.some((row) => row.result)) {
        const result = rows.find((row) => row.result)?.result;
        if (!result) continue;
        const outcome = resultOutcome(workload.responseType, result);
        workload.executionCache.set(key, outcome);
        this.applyOutcome(rows, outcome);
      } else {
        executableGroups.push(group);
      }
    }
    let completedRequests = 0;
    callContext?.reportWorkloadProgress?.({
      kind: "requests",
      completed: completedRequests,
      total: executableGroups.length,
    });
    // Execution is billable and never retried after an ambiguous failure. Safe
    // rejections let siblings settle so a local abort cannot discard results.
    const { controller, cleanup } = createLinkedAbortController(signal);
    const waveSignal = controller.signal;
    try {
      const executeCellBatchQuery =
        this.client.executeCellBatchQuery?.bind(this.client);
      if (executeCellBatchQuery && executableGroups.length > 0) {
        const batches = partition(
          executableGroups,
          TABULAR_CELL_TRANSPORT_BATCH_SIZE,
        );
        const batchConcurrency =
          Math.ceil(
            Math.max(
              TABULAR_CELL_TRANSPORT_BATCH_SIZE,
              this.limits.cellMaxInFlightSingles,
            ) / TABULAR_CELL_TRANSPORT_BATCH_SIZE,
          ) * this.limits.columnMaxInFlightBatches;
        await mapWithConcurrencyLimit(
          batches,
          batchConcurrency,
          async (groups) => {
            groups.forEach((group) => {
              workload.inFlightExecutionKeys.add(group.key);
            });
            let response: ExecuteCellBatchQueryResponse;
            try {
              response = await executeCellBatchQuery(
                this.guided({
                  ...workload.queryOptions,
                  targets: groups.map((group) =>
                    targetForRow(
                      group.representative,
                      this.researchScope.userFileIds,
                    ),
                  ),
                  precomputedFunctionCalls: workload.precomputedFunctionCalls,
                }),
                waveSignal,
                ...optionalLangfuseMetadata(langfuseMetadata),
              );
            } catch (error) {
              if (isSafelyRejectedExecutionFailure(error)) {
                groups.forEach((group) => {
                  workload.inFlightExecutionKeys.delete(group.key);
                });
                throw error;
              }
              if (isAbortFailure(error, waveSignal)) throw error;
              if (isFatalExecutionFailure(error)) {
                throw error;
              }
              groups.forEach((group) => {
                this.acknowledgeExecutionOutcome(
                  workload,
                  group.key,
                  group.rows,
                  {
                    failureCode: "QUERY_EXECUTION_FAILED",
                    transient: isTransientServiceFailure(error),
                  },
                );
              });
              return;
            } finally {
              completedRequests += groups.length;
              callContext?.reportWorkloadProgress?.({
                kind: "requests",
                completed: completedRequests,
                total: executableGroups.length,
              });
            }
            const failuresByTargetIndex = new Map(
              (response.failures ?? []).map((failure) => [
                failure.targetIndex,
                failure,
              ]),
            );
            let resultIndex = 0;
            let firstFatalError: TabularQueryExecutionError | undefined;
            groups.forEach((group, targetIndex) => {
              const failure = failuresByTargetIndex.get(targetIndex);
              if (failure) {
                const error = new TabularQueryExecutionError(
                  failure.kind,
                  "executeCellBatchQuery",
                  failure.message,
                  failure.status,
                );
                if (isFatalExecutionFailure(error)) {
                  if (isSafelyRejectedExecutionFailure(error)) {
                    workload.inFlightExecutionKeys.delete(group.key);
                  } else {
                    // The server reported this target's outcome, so the request
                    // is not ambiguous. Leaving the key in flight would make
                    // resume stamp a known failure QUERY_EXECUTION_AMBIGUOUS.
                    this.acknowledgeExecutionOutcome(
                      workload,
                      group.key,
                      group.rows,
                      { failureCode: "QUERY_EXECUTION_FAILED" },
                    );
                  }
                  firstFatalError ??= error;
                } else {
                  this.acknowledgeExecutionOutcome(
                    workload,
                    group.key,
                    group.rows,
                    {
                      failureCode: "QUERY_EXECUTION_FAILED",
                      transient: isTransientServiceFailure(error),
                    },
                  );
                }
                return;
              }
              const result = response.results[resultIndex];
              resultIndex += 1;
              this.acknowledgeExecutionOutcome(
                workload,
                group.key,
                group.rows,
                result
                  ? resultOutcome(workload.responseType, result)
                  : { failureCode: "QUERY_RESULT_MISSING" },
              );
            });
            if (firstFatalError) throw firstFatalError;
          },
          {
            onFirstFailure: (error) =>
              abortInFlightOnUnsafeFailure(controller, error),
          },
        );
        return;
      }

      await mapWithConcurrencyLimit(
        executableGroups,
        this.limits.cellMaxInFlightSingles,
        async (group) => {
          throwIfAborted(waveSignal);
          const { key, representative, rows } = group;
          workload.inFlightExecutionKeys.add(key);
          try {
            const result = await this.client.executeSingleQuery(
              this.guided({
                ...workload.queryOptions,
                target: targetForRow(
                  representative,
                  this.researchScope.userFileIds,
                ),
                precomputedFunctionCalls:
                  workload.precomputedFunctionCalls,
              }),
              waveSignal,
              ...optionalLangfuseMetadata(langfuseMetadata),
            );
            const acknowledged = {
              group,
              outcome: resultOutcome(workload.responseType, result),
            };
            this.acknowledgeExecutionOutcome(
              workload,
              key,
              rows,
              acknowledged.outcome,
            );
            return acknowledged;
          } catch (error) {
            if (isSafelyRejectedExecutionFailure(error)) {
              workload.inFlightExecutionKeys.delete(key);
              throw error;
            }
            if (isAbortFailure(error, waveSignal)) throw error;
            if (isFatalExecutionFailure(error)) {
              throw error;
            }
            const acknowledged = {
              group,
              outcome: {
                failureCode: "QUERY_EXECUTION_FAILED" as RowFailureCode,
                transient: isTransientServiceFailure(error),
              },
            };
            this.acknowledgeExecutionOutcome(
              workload,
              key,
              rows,
              acknowledged.outcome,
            );
            return acknowledged;
          } finally {
            completedRequests += 1;
            callContext?.reportWorkloadProgress?.({
              kind: "requests",
              completed: completedRequests,
              total: executableGroups.length,
            });
          }
        },
        {
          onFirstFailure: (error) =>
            abortInFlightOnUnsafeFailure(controller, error),
        },
      );
    } finally {
      cleanup();
    }
  }

  private acknowledgeExecutionOutcome(
    workload: TabularWorkload,
    key: string,
    rows: readonly TabularWorkloadRow[],
    outcome: StoredExecutionOutcome,
  ): void {
    workload.executionCache.set(key, outcome);
    workload.inFlightExecutionKeys.delete(key);
    this.applyOutcome(rows, outcome);
    this.version += 1;
  }

  /**
   * Reopen the rows an outage closed so the wave can be executed again. The
   * cached outcome is dropped with the row failure because execution reuses a
   * cached outcome for the same company instead of calling the service.
   */
  private discardWaveServiceFailures(
    workload: TabularWorkload,
    waveRows: readonly TabularWorkloadRow[],
  ): number {
    let reopened = 0;
    for (const row of waveRows) {
      if (row.failureCode !== "QUERY_EXECUTION_FAILED") continue;
      delete row.failureCode;
      delete row.transientFailure;
      workload.executionCache.delete(executionIdentityKey(row));
      reopened += 1;
    }
    if (reopened > 0) this.version += 1;
    return reopened;
  }

  private prepareInterruptedWaveForResume(
    workload: TabularWorkload,
    waveRows: readonly TabularWorkloadRow[],
  ): void {
    for (const row of waveRows) {
      if (
        !row.identity ||
        !workload.inFlightExecutionKeys.has(executionIdentityKey(row))
      ) {
        continue;
      }
      row.failureCode = "QUERY_EXECUTION_AMBIGUOUS";
      delete row.result;
      delete row.preparedWrite;
    }
    workload.inFlightExecutionKeys.clear();
  }

  private preserveInterruptedWave(): void {
    const workload = this.workload;
    if (!workload || workload.activeWaveRows.length === 0) return;
    if (!workload.waveInterrupted) {
      workload.waveInterrupted = true;
      this.version += 1;
    }
  }

  private applyOutcome(
    rows: readonly TabularWorkloadRow[],
    outcome: StoredExecutionOutcome,
  ): void {
    for (const row of rows) {
      if (outcome.result) {
        row.result = outcome.result;
        delete row.failureCode;
        delete row.transientFailure;
      } else {
        row.failureCode =
          outcome.failureCode ?? "QUERY_EXECUTION_FAILED";
        if (outcome.transient) {
          row.transientFailure = true;
        } else {
          delete row.transientFailure;
        }
        delete row.result;
        delete row.preparedWrite;
      }
    }
  }

  private validateWorkloadId(workloadId: string): string | null {
    if (!this.workload) {
      return (
        `No workload is registered. Call ` +
        `${START_TABULAR_QUERY_WORKLOAD_TOOL_NAME} first.`
      );
    }
    if (!workloadId || workloadId !== this.workload.id) {
      return `Unknown workloadId "${workloadId}". Active workload: ${this.workload.id}.`;
    }
    return null;
  }

  private dateSystemCacheKey(): string {
    return this.ctx.session.pinnedWorkbookName?.trim().toLowerCase() ?? "";
  }

  private resolveDateSystem(
    workload: Pick<TabularWorkload, "sheetName">,
  ): Promise<ExcelDateSystem | null> {
    const pinnedWorkbookName = this.ctx.session.pinnedWorkbookName?.trim();
    const cacheKey = this.dateSystemCacheKey();
    let promise = this.dateSystemPromises.get(cacheKey);
    if (!promise) {
      const probe: (
        options?: ExcelDateSystemProbeOptions,
      ) => Promise<ExcelDateSystem | null> =
        this.ctx.probeExcelDateSystem ?? probeExcelDateSystem;
      promise = Promise.resolve()
        .then(() =>
          probe({
            pinnedWorkbookName,
            sheetName: workload.sheetName,
            signal: this.ctx.signal,
          }),
        )
        .catch(() => null)
        .then((dateSystem) => {
          this.dateSystemOutcomes.set(cacheKey, dateSystem);
          return dateSystem;
        });
      this.dateSystemPromises.set(cacheKey, promise);
    }
    return promise;
  }

  private buildSummary(): Record<string, unknown> {
    const workload = this.workload;
    if (!workload) {
      return { ok: false, error: "No tabular query workload is registered." };
    }
    const pendingRows = workload.rows.filter(
      (row) => row.status === "pending",
    ).length;
    const activeRows = workload.rows.filter(
      (row) => row.status === "active",
    ).length;
    const closedRows = workload.rows.filter(
      (row) => row.status === "closed",
    ).length;
    const failedRows = workload.rows.filter(
      (row) => row.status === "closed" && row.failureCode,
    ).length;
    return {
      ok: true,
      workloadId: workload.id,
      sheetName: workload.sheetName,
      companyAddress: workload.companyAddress,
      companyIdAddress: workload.companyIdAddress,
      outputAddress: workload.outputAddress,
      mode: workload.mode,
      totalRows: workload.rows.length,
      pendingRows,
      activeRows,
      closedRows,
      usableRows: this.verifiedUsableRows,
      failedRows,
      remainingRows: workload.rows.length - closedRows,
      activeWaveRows: [...workload.activeWaveRows],
      limits: { ...this.limits },
      blocked: workload.blocked,
      complete: this.isComplete,
      ...(workload.prefilledOutputRows.length > 0
        ? { skippedPrefilledRows: workload.prefilledOutputRows.length }
        : {}),
    };
  }

  private releaseOutputScopeIfComplete(): void {
    const workload = this.workload;
    if (!workload || !this.isComplete) return;
    this.releaseOutputScope(workload.id);
    this.detachAbortListener();
  }

  private releaseOutputScope(workloadId: string): void {
    if (
      this.ctx.worksheetWorkloadOutputScope?.workloadId === workloadId
    ) {
      delete this.ctx.worksheetWorkloadOutputScope;
    }
  }

  private attachAbortListener(): void {
    if (
      this.abortListenerAttached ||
      !this.ctx.signal ||
      this.ctx.signal.aborted
    ) {
      return;
    }
    this.ctx.signal.addEventListener("abort", this.handleRuntimeAbort, {
      once: true,
    });
    this.abortListenerAttached = true;
  }

  private detachAbortListener(): void {
    if (!this.abortListenerAttached || !this.ctx.signal) return;
    this.ctx.signal.removeEventListener("abort", this.handleRuntimeAbort);
    this.abortListenerAttached = false;
  }
}
